/**
 * Timbrica Search Engine — единый ранжирующий движок для поиска по инструментам.
 *
 * Используется и хедер-дропдауном (public/js/site-search.js), и страницей
 * /search. Раньше это были два разных скоринга (хедер — без IDF, страница — с
 * IDF), и одна и та же строка давала разные ответы в двух местах.
 *
 * Что он умеет сверх substring-матчинга, который был раньше:
 *   - морфология: «песню» → «песня», «обрезать» → «обрезка», «звучания» → «звук»
 *     (лёгкий стеммер RU/EN + допуск на чередование в конце основы);
 *   - покрытие запроса: инструмент, отвечающий на ВСЕ слова, всегда выше того,
 *     кто поймал одно. «тест звучания» больше не возвращает 97 любых «тестов»;
 *   - префикс: живой ввод («подпи» → «Подписать PDF») — отдельный класс матча,
 *     а не случайное попадание подстроки;
 *   - популярность как тай-брейк, когда релевантность равная;
 *   - синонимы разговорных формулировок («колонки» → динамики, «гифка» → GIF);
 *   - направление: «голос в текст» ≠ «текст в голос» — роли «вход → выход» по
 *     типам данных у запроса и у инструмента (см. «Направление: из ЧЕГО во ЧТО»).
 *
 * Экспорт: window.TimbricaSearch (браузер) + module.exports (бенчмарк на node).
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.TimbricaSearch = api;
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : null), function () {
    'use strict';

    /* ------------------------------------------------------------------ *
     * Нормализация и токенизация
     * ------------------------------------------------------------------ */

    // Буквы/цифры любой письменности + внутрисловные дефис/точка не режем:
    // «mp3», «5.1», «ipv6», «e-mail» должны оставаться одним токеном.
    //
    // ⚠️ Регулярка СТРОИТСЯ, а не пишется литералом. Литерал /[^\p{L}\p{N}]+/u
    // разбирается движком при загрузке файла, и на движке без поддержки
    // юникод-свойств (ES2018) это SyntaxError на этапе разбора — то есть
    // ВЕСЬ модуль поиска не выполняется, а не одна строка. Измерено в Sentry
    // 08.08.2026: UC Browser 13.4 на Android 14, «Invalid property name in
    // character class», поиск по сайту у этих людей просто не работал.
    // new RegExp() переносит ту же ошибку в РАНТАЙМ, где её можно поймать.
    //
    // Запасной вариант перечисляет диапазоны письменностей ЯВНО. Он обязан
    // покрывать все локали сайта, иначе на старом движке поиск не «огрубится»,
    // а просто перестанет находить: буквы вне класса становятся разделителями,
    // и от корейского или арабского запроса не остаётся ни одного токена.
    // Проверено на строках всех девяти локалей (см. коммит).
    var SPLIT_RE;
    try {
        SPLIT_RE = new RegExp('[^\\p{L}\\p{N}]+', 'u');
    } catch (e) {
        SPLIT_RE = new RegExp('[^0-9A-Za-z'
            + 'À-ɏ'   // латиница с диакритикой (de/es/fr/pt/id)
            + 'Ͱ-Ͽ'   // греческий
            + 'Ѐ-ӿ'   // кириллица (ru)
            + '֐-׿'   // иврит
            + '؀-ۿݐ-ݿ'   // арабский (ar)
            + 'ऀ-ॿ'   // деванагари
            + '฀-๿'   // тайский
            + 'ᄀ-ᇿ㄰-㆏가-힯'   // хангыль (ko)
            + '぀-ヿ'   // кана
            + '一-鿿'   // иероглифы
            + ']+');
    }

    /**
     * Арабский пишется с вариативной орфографией, и до 27.08.2026 движок этого
     * не знал: «ازالة» и «إزالة» — одно слово, но для индекса это были разные
     * строки. Живой клик-лог: человек набрал «ازالة» (без хамзы, как обычно
     * печатают), а «إزالة الصوت من الفيديو» стояла на 12-м месте — нашлась
     * только по ключевым словам, не по названию.
     *
     * Сводим ровно то, что различается лишь начертанием: огласовки (их в вводе
     * не ставят), татвиль-растяжку, формы алифа/хамзы, «та марбуту» и «алиф
     * максуру». Это НЕ стемминг — форма слова не меняется, меняется только его
     * написание.
     */
    // Свёртка написана \u-экранированием: половина этих знаков —
    // огласовки и татвиль, то есть небуквенные метки, которых в исходнике не
    // видно глазом и которые молча пропадают при любой правке строки.
    var AR_RE = /[\u0600-\u06FF]/;
    function foldArabic(s) {
        return s
            .replace(/[\u064B-\u0652\u0670\u0640]/g, '')            // огласовки, татвиль
            .replace(/[\u0622\u0623\u0625\u0671]/g, '\u0627')   // آ أ إ ٱ → ا
            .replace(/\u0629/g, '\u0647')                        // ة → ه
            .replace(/\u0649/g, '\u064A')                        // ى → ي
            .replace(/\u0624/g, '\u0648')                        // ؤ → و
            .replace(/\u0626/g, '\u064A');                       // ئ → ي
    }

    /**
     * Латинская диакритика: «musica» и «música» — одно слово.
     *
     * Половина наших локалей пишется со знаками, которых нет на телефонной
     * клавиатуре по умолчанию и которые почти никто не набирает в поиске:
     * pt «compressão», es «compresión», fr «réduction», de «größe». В индексе
     * они стоят (это переводы), в запросе — нет, и слово просто не совпадает:
     * ни основа, ни префикс, ни фраза. Живой промах: «digitacao» (8 сессий),
     * «digitaçao», «depreção» — ни одного результата при живых
     * «digitação»/«depressão» в каталоге.
     *
     * Свёртка симметрична (индекс и запрос идут через ОДИН normalize), поэтому
     * точность она не трогает: два написания одного слова становятся одной
     * строкой, разные слова не сливаются. NFD → снять комбинирующие знаки →
     * NFC: возврат в NFC обязателен, иначе хангыль (он тоже раскладывается)
     * остался бы набором чамо, а он на своей локали ничем не болен.
     *
     * Лигатуры и перечёркнутые буквы NFD не раскладывает — их пять, они
     * перечислены руками (ß→ss — немецкая норма транслитерации, её же даёт
     * поиск Google).
     */
    var LAT_DIA = /[\u00C0-\u024F\u1E00-\u1EFF]/;
    var LAT_LIG = /[\u00DF\u00E6\u0153\u00F8\u0142\u0111\u00F0\u00FE]/;
    function foldLatin(s) {
        var out = s.normalize ? s.normalize('NFD').replace(new RegExp('[\u0300-\u036F]', 'g'), '').normalize('NFC') : s;
        if (LAT_LIG.test(out)) {
            out = out
                .replace(new RegExp('\u00DF', 'g'), 'ss')   // ß
                .replace(new RegExp('\u00E6', 'g'), 'ae')   // æ
                .replace(new RegExp('\u0153', 'g'), 'oe')   // œ
                .replace(new RegExp('\u00F8', 'g'), 'o')    // ø
                .replace(new RegExp('\u0142', 'g'), 'l')    // ł
                .replace(new RegExp('\u0111', 'g'), 'd')    // đ
                .replace(new RegExp('\u00F0', 'g'), 'd')    // ð
                .replace(new RegExp('\u00FE', 'g'), 'th');  // þ
        }
        return out;
    }

    function normalize(s) {
        var out = String(s || '')
            .toLowerCase()
            .replace(/ё/g, 'е')   // ё → е
            .replace(/й/g, 'и');  // й → и (тайминг/таиминг, мой/мои)
        // Гейт по наличию арабских букв: normalize() зовётся на КАЖДЫЙ токен
        // каждого документа, и шесть замен вхолостую на восьми локалях из
        // девяти — это цена prepare() в главном потоке. Та же логика у
        // латинской диакритики: тест стоит доли микросекунды, а NFD платят
        // только те токены, где знак действительно есть.
        if (AR_RE.test(out)) out = foldArabic(out);
        if (LAT_DIA.test(out)) out = foldLatin(out);
        return out.trim();
    }

    function tokenize(s) {
        var out = [], parts = normalize(s).split(SPLIT_RE);
        for (var i = 0; i < parts.length; i++) if (parts[i]) out.push(parts[i]);
        return out;
    }

    /* ------------------------------------------------------------------ *
     * Стемминг
     * ------------------------------------------------------------------ */

    var RU_RE = /[а-я]/;

    // Порядок важен: длинные окончания раньше коротких.
    //
    // ⚠️⚠️ Список ОБЯЗАН пройти через normalize() — он написан в словарной
    // форме, а до стеммера слово доходит уже нормализованным (й→и, ё→е).
    // Пока `.map(normalize)` не было, КАЖДОЕ окончание с «й» — 'ей', 'ой',
    // 'ый', 'ий' — не могло совпасть никогда: в нормализованном слове буквы
    // «й» уже нет. Слово доезжало до одиночного 'и' в самом хвосте списка и
    // получало основу на букву длиннее, чем у своих же других форм:
    //
    //     изображения → 'изображ'    (сработало 'ения')
    //     изображений → 'изображени' (дошло до 'и')
    //     красный → 'красны'   vs  красная → 'красн'
    //     ключей  → 'ключе'    vs  ключ    → 'ключ'
    //
    // Для поиска это РАЗНЫЕ слова: основы не равны, `stemsNear` их тоже не
    // сводит (он требует равной длины). То есть промахивался весь класс
    // прилагательных мужского рода и родительный падеж множественного числа —
    // на сайте, где 90% запросов русские.
    //
    // Живой счёт: заявка #22 (14.07) просила «создать» Редактор метаданных
    // изображения — инструмент, который у нас есть и который на запросе
    // «редактор метаданных изображений» стоял ВТОРЫМ, под Редактором
    // метаданных аудио: третье слово не совпадало ни с чем, и решала
    // популярность.
    //
    // ⚠️ Второе: список срезает словообразовательный хвост целиком
    // («изображение» → 'изображ'), но делал это ТОЛЬКО у словарной формы —
    // «изображений» терял одно падежное окончание и давал 'изображен'. Значит
    // косвенным падежам -ание/-ение и группе -ие/-ия нужны СВОИ записи, иначе
    // формы одного слова снова расходятся — та же поломка, этажом ниже.
    //
    // ⚠️ Третье: список СОРТИРУЕТСЯ по длине. Заголовок «длинные окончания
    // раньше коротких» описывал намерение, а не факт: 'ся'/'сь' стояли ВЫШЕ
    // 'ание'/'ения', и достаточно одного нового длинного окончания в
    // «неправильном» месте, чтобы его молча перекрыло короткое. Сортировка
    // стабильная, поэтому порядок внутри одной длины остаётся авторским.
    //
    // Набор выбран ЗАМЕРОМ, а не на глаз (tests/scratch — сравнивались шесть
    // вариантов на голдсете + на группах словоформ). Отвергнуто:
    //   - снять словообразовательные вовсе (-ание/-ение/-ство/-тель): рвётся
    //     «удаление» ↔ «удалить», голдсет top1 95.1% → 92.7%;
    //   - снять только -ство/-тель: морфология лучше, но голдсет теряет
    //     «увеличить качество видео» (95.1% → 93.9%).
    var RU_SUFFIX = [
        'аниями', 'ениями', 'аниях', 'ениях', 'аниям', 'ениям', 'анием', 'ением',
        'ание', 'ения', 'ение', 'ания', 'анию', 'ению', 'ании', 'ении', 'аний', 'ений',
        'ство', 'тель', 'ием', 'иям', 'иях', 'ия', 'ию', 'ий',
        'иями', 'ыми', 'ими', 'ами', 'ями', 'ого', 'его', 'ому', 'ему', 'ых', 'их',
        'ать', 'ять', 'еть', 'ить', 'уть', 'ыть', 'ся', 'сь',
        'ов', 'ев', 'ей', 'ой', 'ый', 'ий', 'ая', 'яя', 'ое', 'ее', 'ые', 'ие',
        'ам', 'ям', 'ах', 'ях', 'ом', 'ем', 'ум', 'ю', 'я', 'и', 'ы', 'а', 'о', 'е', 'у', 'ь'
    ].map(normalize)
        // Разные словарные формы схлопываются в одну после normalize()
        // ('ений' и 'ении' → 'ении', 'ий' → 'ии'). Обе записи в списке
        // осмысленны — это разные падежи, — но в рантайме дубль бесполезен.
        .filter(function (s, i, a) { return a.indexOf(s) === i; })
        .sort(function (a, b) { return b.length - a.length; });

    var EN_SUFFIX = ['ing', 'ers', 'ed', 'es', 'er', 's'];

    /**
     * Арабский — не «латиница по умолчанию», как считал стеммер до 27.08.2026:
     * английские окончания к нему неприменимы, то есть морфологии у арабской
     * локали не было вообще.
     *
     * Определённый артикль «ال» приклеивается к слову спереди, и без его снятия
     * «الشاشة» (экран) и «شاشة» (экран) — разные слова; в названиях
     * инструментов артикль есть, в запросах его чаще нет. Суффиксы —
     * множественное число и местоименные окончания. Список короткий намеренно:
     * настоящая арабская морфология шаблонная (корень + огласовки), лёгким
     * срезом её не покрыть, а каждое лишнее окончание склеивает разные корни.
     *
     * Порядок: сначала артикль, потом окончание — «الصور» → «صور».
     */
    var AR_PREFIX = ['وال', 'بال', 'لل', 'ال'];
    var AR_SUFFIX = ['ية', 'ات', 'ين', 'ون', 'ها', 'ان', 'كم', 'هم', 'نا', 'ه', 'ي', 'ك'];

    var MIN_STEM = 3;

    /**
     * Окончания разложены по ДЛИНЕ, и поиск идёт от самого длинного к самому
     * короткому. Это ровно то же правило «длинное окончание раньше короткого»
     * — просто выраженное так, что цена перестала зависеть от размера списка.
     *
     * Сплошной перебор — это `slice(-n)` (новая строка) на КАЖДОЕ окончание
     * для КАЖДОГО токена индекса, то есть ~70 срезов на слово. Пока список был
     * коротким, это терпелось; косвенные падежи -ание/-ение и группа -ие/-ия
     * его удлинили, и prepare() на RU-индексе подорожал вдвое — а он идёт на
     * первом же касании поиска, в главном потоке, и на телефоне это втрое
     * дороже. Здесь срезов максимум 6 (длина самого длинного окончания), а
     * проверка — обращение по ключу.
     *
     * Внутри одной длины окончания различны, поэтому совпасть может только
     * одно: результат побитово тот же, что у перебора. Проверено сверкой на
     * 37 592 токенах RU+EN индексов — расхождений 0.
     */
    function bySize(list) {
        var by = Object.create(null), max = 0, i, s;
        for (i = 0; i < list.length; i++) {
            s = list[i];
            if (!by[s.length]) by[s.length] = Object.create(null);
            by[s.length][s] = 1;
            if (s.length > max) max = s.length;
        }
        return { by: by, max: max };
    }

    var RU_BY_LEN = bySize(RU_SUFFIX);
    var EN_BY_LEN = bySize(EN_SUFFIX);

    var AR_BY_LEN = null;
    function stemArabic(word) {
        for (var i = 0; i < AR_PREFIX.length; i++) {
            var pre = AR_PREFIX[i];
            if (word.length - pre.length >= MIN_STEM && word.lastIndexOf(pre, 0) === 0) {
                word = word.slice(pre.length);
                break;
            }
        }
        if (word.length <= MIN_STEM) return word;
        if (!AR_BY_LEN) AR_BY_LEN = bySize(AR_SUFFIX);
        var len = word.length - MIN_STEM;
        if (len > AR_BY_LEN.max) len = AR_BY_LEN.max;
        for (; len >= 1; len--) {
            var bucket = AR_BY_LEN.by[len];
            if (bucket && bucket[word.slice(-len)] === 1) return word.slice(0, word.length - len);
        }
        return word;
    }

    /**
     * Существительные, которые стеммер читает как глагол на «-ать/-ять/-еть»
     * и срезает до обрубка: «печать» → «печ», «память» → «пам», «нейросеть» →
     * «неирос», — а их же косвенные падежи «печати», «памяти», «нейросети»
     * дают «печат», «памят», «неиросет». Для поиска это РАЗНЫЕ слова: запрос
     * «печать» не находил «Конструктор печатей» и «Калькулятор 3D-печати»
     * вовсе — решала популярность среди инструментов, где «печать» стоит
     * буквально (замер 29.09.2026 по письму «Что хотят сделать»: «3д печать» —
     * «Фоторедактор» первым). Общим правилом эти слова от глаголов не отличить
     * («печатать» — глагол), поэтому список — из словаря индекса: слова на
     * «-ать/-ять/-еть» от шести букв, у которых в индексе есть падежные формы.
     */
    var STEM_KEEP = {
        'печать': 'печат', 'печатью': 'печат',
        'память': 'памят', 'памятью': 'памят',
        'неиросеть': 'неиросет', 'неиросетью': 'неиросет'
    };

    function stem(word) {
        if (AR_RE.test(word)) return stemArabic(word);
        if (word.length <= MIN_STEM) return word;
        if (STEM_KEEP[word]) return STEM_KEEP[word];
        var S = RU_RE.test(word) ? RU_BY_LEN : EN_BY_LEN;
        var len = word.length - MIN_STEM;
        if (len > S.max) len = S.max;
        for (; len >= 1; len--) {
            var bucket = S.by[len];
            if (bucket && bucket[word.slice(-len)] === 1) {
                return word.slice(0, word.length - len);
            }
        }
        return word;
    }

    /**
     * Основы «почти совпадают» — покрывает чередование, которое стеммер не
     * сводит: звук/звучание даёт «звук»/«звуч».
     *
     * Правило намеренно узкое: РАВНАЯ длина и расхождение ровно в последнем
     * символе. Более мягкий вариант (длины ±1) склеивал «trigg» (от triggers) с
     * «trim» — из-за этого «trim song» на intl вытаскивал Anxiety Tracker и
     * GPS Speedometer.
     */
    function stemsNear(a, b) {
        if (a === b) return true;
        if (a.length !== b.length || a.length < 4) return false;
        for (var i = 0; i < a.length - 1; i++) if (a[i] !== b[i]) return false;
        return true;
    }

    /* ------------------------------------------------------------------ *
     * Синонимы — разговорные формулировки, которых нет в названиях
     * ------------------------------------------------------------------ */

    // ВАЖНО: ключи — это ОСНОВЫ (то, что вернёт stem()), а не словарные формы.
    // «звучания» доходит сюда как «звуч», и ключ 'звучан' не сработает.
    var SYNONYMS = {
        ru: {
            'звуч': ['звук', 'аудио', 'динамик', 'наушник', 'колонк'],
            'колонк': ['динамик', 'звук', 'аудио'],
            'наушник': ['динамик', 'звук'],
            'гифк': ['gif'], 'гиф': ['gif'],
            'ролик': ['видео'], 'видос': ['видео'],
            'песн': ['аудио', 'музык', 'трек'],
            'трек': ['аудио', 'музык'],
            'мелоди': ['музык', 'аудио'],
            'фотк': ['фото', 'изображен'], 'картинк': ['изображен', 'фото'],
            'ноутбук': ['компьютер'], 'комп': ['компьютер'],
            'мобильн': ['смартфон', 'телефон'],
            'громкост': ['громкост', 'звук', 'уровен'],
            'обрез': ['обрезк', 'вырез', 'подрез'],
            'вырез': ['обрезк', 'удал', 'извлеч'],
            'убра': ['удал', 'очистк', 'извлеч'], 'удали': ['удал'],
            'сдела': ['созда', 'генератор', 'конструктор'],
            'созда': ['генератор', 'конструктор'],
            'придум': ['генератор', 'созда'],
            'помен': ['измен', 'смен'], 'поменя': ['измен', 'смен'],
            'настро': ['тюнер', 'настройк', 'калибровк'],
            'провер': ['тест', 'проверк'], 'проверк': ['тест', 'провер'],
            'тест': ['провер', 'проверк'],
            'перевод': ['переводчик'],
            'молитв': ['намаз'],
            'сжат': ['сжима', 'уменьш', 'компресс'],
            'уменьш': ['сжат', 'размер'],
            'увелич': ['разрешен', 'апскейл', 'улучшен'],
            'распозна': ['распознаван', 'определител'],
            // Замерено по клик-логу и промахам за 120 дней. Здесь только
            // ЯЗЫКОВЫЕ соответствия — «отделить» ≈ «разделить», «соединить» ≈
            // «объединить». Признак принадлежности сюда: пара верна во всём
            // каталоге. Свойство КОНКРЕТНОГО инструмента («затухание» — это
            // /volume, «синтез речи» — это /text-to-speech) синонимом не
            // лечится: оно живёт в `search_kw` этого инструмента, иначе
            // движок начинает знать про тулы и расходится с каталогом.
            // Живой случай: «отделить» (жалоба 27.08) находило РОВНО ОДИН
            // инструмент на весь каталог.
            'отдел': ['раздел', 'извлеч', 'вырез', 'удал'],
            // «Вытащить» — то же действие, что «извлечь»: живой лог даёт
            // «вытащить музыку», «вытащить голос», «вытащить звук». Пара
            // языковая, во всём каталоге верна.
            'вытащ': ['извлеч', 'удал', 'вырез'],
            'изолир': ['раздел', 'извлеч', 'удал'],
            'изоляц': ['раздел', 'извлеч'],
            'склеи': ['объедин', 'соедин', 'склейк'],
            // Отглагольное существительное даёт ДРУГУЮ основу: «склеить» →
            // «скле», «склеивание» → «склеив». Для стеммера это разные слова,
            // поэтому запись нужна отдельная — иначе «склеивание видео» не
            // находит «Склеить видео».
            'склеив': ['объедин', 'соедин', 'склейк', 'скле'],
            'соедин': ['объедин', 'склейк'],
            'слеп': ['объедин', 'склейк'],
            'сочин': ['созда', 'генератор'],
            'написа': ['созда', 'генератор', 'текст'],
            'диктофон': ['запис', 'диктофон'],
            'озвуч': ['озвучк', 'голос', 'речь'],
            'слух': ['слух', 'аудиометр'],
            // Ремонт. Класс «починить сломанный файл» зовётся в каталоге
            // четырьмя словами вразнобой — ремонт / починка / восстановление /
            // исправление, — а человек набирает любое из них: «ремонт видео»
            // (Фарид, 09.09.2026) не находил /repair-video, у которого в
            // словаре стояли только «восстановить» и «починить». Пара верна
            // во всём каталоге, поэтому живёт здесь, а не в search_kw.
            'ремонт': ['починить', 'починка', 'восстановить', 'восстановление', 'исправить'],
            'почин': ['ремонт', 'восстановить', 'восстановление', 'исправить'],
            'починк': ['ремонт', 'восстановить', 'восстановление', 'исправить'],
            'чин': ['починить', 'ремонт', 'восстановить'],
            'восстанов': ['ремонт', 'починить', 'исправить'],
            'восстановл': ['ремонт', 'починка', 'исправление'],
            'исправ': ['починить', 'ремонт', 'восстановить'],
            'реставрац': ['восстановление', 'ремонт'],
            'сломан': ['повреждённый', 'битый', 'ремонт'],
            'сломанн': ['повреждённый', 'битый', 'ремонт'],
            'сломал': ['повреждённый', 'битый', 'ремонт'],
            // Доставить файл с устройства на устройство. Класс зовётся в
            // русском вразнобой — передать / переслать / отправить / скинуть /
            // перекинуть / обмен, — а в каталоге он назван ОДИН раз:
            // «передача» (плюс «перенос» в ключевых словах). Сверх того формы
            // одного слова расходятся у самого стеммера: «передача» →
            // «передач», а «передать» → «перед», поэтому ключей здесь больше,
            // чем слов в классе. Замер 09.09.2026 на RU-индексе:
            // /qr-file-transfer находился ТОЛЬКО по дословному «передача
            // файлов»; «передать файл» отдавал «Удаление метаданных»,
            // «отправить файл» — «Просмотр снимка зубов». Пара верна во всём
            // каталоге, поэтому живёт здесь, а не в search_kw одного тула.
            // ⛔ «поделиться» в класс НЕ входит, хотя просится: слово одинаково
            // обслуживает экран, местоположение и ссылку, и с ним «поделиться
            // экраном» отдавало передачу файлов выше «Демонстрации экрана».
            // ⚠️ У общих слов («отправить», «скинуть», «обмен») оставлена одна
            // «передача»: «перенос» стоит ещё и в ключевых словах «Дат фото из
            // Takeout», и на «отправить фото по почте» вытаскивал её второй.
            'перед': ['передача', 'перенос'],
            'пересл': ['передача', 'перенос', 'отправить'],
            'перешл': ['передача', 'перенос'],
            'пересылк': ['передача', 'перенос'],
            'отправ': ['передача'],
            'отправк': ['передача'],
            'отправля': ['передача'],
            'скин': ['передача'],
            'перекин': ['передача', 'перенос'],
            'перенест': ['перенос', 'передача'],
            'обмен': ['передача'],
            // Мат. Каталог зовёт класс одним словом — «мат» («Удаление мата из
            // аудио / из видео», «убрать мат» в ключах), а человек набирает
            // прилагательное или синоним: «хочу убрать матерные слова» отдал
            // «Удаление слова из песни» первым, «Удаление мата» — пятым;
            // «хочу убрать матер» — ни одного тула про мат в тройке; «убрать
            // нецензурную брань» — ни одного в шестёрке (журнал поиска RU, 90
            // дней, и индекс прода 28.10.2026). Ключи — основы ЭТИХ слов, и
            // формы одного слова стеммер разводит («нецензурные» → «нецензурн»,
            // «нецензурную» → «нецензурну»). ⛔ Голое «матер» не ключ: это и
            // «матери» («сценарий на день матери» — живой запрос).
            'матерн': ['мат'], 'матерщин': ['мат'],
            'нецензурн': ['мат'], 'нецензурну': ['мат'], 'нецензурщин': ['мат'],
            'бран': ['мат'], 'бранн': ['мат'],
            'ругательств': ['мат'], 'ругатель': ['мат'], 'руган': ['мат']
        },
        en: {
            'pic': ['image', 'photo'], 'pics': ['image', 'photo'],
            'vid': ['video'], 'clip': ['video'],
            // без 'track': омоним — цепляет Anxiety Tracker и GPS-трекеры
            'song': ['audio', 'music'],
            'sound': ['audio'],
            'speaker': ['audio', 'sound'],
            'shrink': ['compress', 'reduc'],
            'cut': ['trim', 'crop'],
            // «put music on video» — то же действие, что «add». Замер по
            // клик-логу: запрос уводил в Video Overlay и Video Looper, а
            // человек открывал Add Music to Video.
            'put': ['add', 'insert', 'overlay'],
            // «Isolate the vocals» = «separate / extract». Основа у английского
            // стеммера не срезает -ion и -e, поэтому обе формы — отдельные
            // ключи, иначе половина написаний молчит.
            'isolate': ['separate', 'extract', 'split', 'remove'],
            'isolation': ['separate', 'extract', 'split', 'remove'],
            'check': ['test'], 'tester': ['test'],
            'denois': ['noise', 'clean'],
            'background': ['noise', 'denois'],
            // Same repair class as the RU block above.
            'repair': ['fix', 'restore', 'recover'],
            'fix': ['repair', 'restore', 'recover'],
            'restore': ['repair', 'recover', 'fix'],
            'recov': ['repair', 'restore', 'fix'],
            'recovery': ['repair', 'restore', 'recover'],
            'broken': ['corrupted', 'damaged', 'repair'],
            'corrupt': ['broken', 'damaged', 'repair']
        }
    };

    function synonymDict(locale) {
        return SYNONYMS[locale] || SYNONYMS[locale === 'ru' ? 'ru' : 'en'] || {};
    }

    // Глагол МЕРЫ рядом со словом «скорость» — это «замедлить» или «ускорить».
    // Сам по себе глагол значит другое: «уменьшить фото» — размер, «увеличить
    // громкость» — уровень, поэтому в SYNONYMS ему не место (там пара обязана
    // быть верной во всём каталоге). Верна во всём каталоге ПАРА «глагол +
    // скорость». Отзыв 13.10.2026: «уменьшить скорость» отдавал первым
    // «Дневник родинок» («уменьш» ≈ «размер» + «скорость роста» в описании), а
    // «Редактора скорости видео» не было в восьмёрке; «сбавить скорость» —
    // «Реставрацию фото» (слова «сбавить» каталог не знает, и починка опечаток
    // переписывала его до синонимов). Замер на индексах прода — docs/why/search.md.
    var MEASURE_VERBS = {
        ru: {
            obj: ['скорост', 'скорость', 'темп'],
            verb: { 'уменьш': 'замедл', 'сниз': 'замедл', 'сниж': 'замедл', 'пониз': 'замедл', 'пониж': 'замедл',
                    'сбав': 'замедл', 'убав': 'замедл',
                    'увелич': 'ускор', 'повыс': 'ускор', 'повыш': 'ускор', 'подн': 'ускор' }
        },
        en: {
            obj: ['spe', 'speed', 'tempo'],
            verb: { 'decrease': 'slow', 'decreas': 'slow', 'reduce': 'slow', 'reduc': 'slow', 'low': 'slow', 'lower': 'slow',
                    'increase': 'fast', 'increas': 'fast', 'raise': 'fast', 'boost': 'fast' }
        }
    };

    function measureVerb(stems, i, locale) {
        var m = MEASURE_VERBS[locale];
        if (!m || !m.verb[stems[i]]) return '';
        for (var k = 0; k < stems.length; k++) if (m.obj.indexOf(stems[k]) !== -1) return m.verb[stems[i]];
        return '';
    }

    function expandQuery(stems, locale) {
        var dict = synonymDict(locale);
        var extra = [];
        for (var i = 0; i < stems.length; i++) {
            var to = measureVerb(stems, i, locale);
            if (to) { extra.push({ w: stem(to), from: i }); continue; }
            var syn = dict[stems[i]];
            if (syn) for (var j = 0; j < syn.length; j++) extra.push({ w: stem(syn[j]), from: i });
        }
        return extra;
    }

    /* ------------------------------------------------------------------ *
     * Направление: из ЧЕГО во ЧТО
     *
     * Скоринг читает запрос как мешок слов, и для «голос в текст» и «текст в
     * голос» мешок один и тот же. Выдачу решала популярность, то есть почти
     * всегда ОБРАТНАЯ операция: замер 23.09.2026 на эталоне фраз-задач
     * (tests/fixtures/search-goldset-tasks.json, живые запросы, разметка по
     * смыслу): «голос в текст» → Озвучка текста, «аудио в видео» → Извлечь
     * аудио из видео, «текст в видео» → Убрать текст из видео, «ноты в аудио» →
     * Аудио в ноты, «mp4 to mp3» → Конвертер файлов. Треть промахов эталона —
     * этот один класс.
     *
     * Лечится не словами, а ролями. Слово приводится к ТИПУ данных (аудио,
     * речь, текст, видео, фото, ноты…), и у запроса, и у инструмента
     * извлекаются пары «вход → выход». Типы, а не основы, потому что
     * «mp3 в видео», «песню в клип» и «аудио в ролик» — одна и та же пара.
     *
     * Три конструкции различаются намеренно — у них разный смысл:
     *   - ПРЕОБРАЗОВАНИЕ «X в Y», «из X в Y», «Y из X», «X to Y», «de X a Y»:
     *     вход X, выход Y. Только здесь порядок решает ответ.
     *   - ПРИКРЕПЛЕНИЕ «добавить X к Y», «X на Y», «put X on Y»: у результата
     *     тип Y, и информативен только Y — «текст на картинку» не про видео.
     *   - УДАЛЕНИЕ «убрать X из Y» / «убрать X в Y»: предлог здесь ничего не
     *     говорит о направлении («убрать текст в видео» = «убрать текст из
     *     видео»), поэтому такие фразы ролей не дают вовсе. Без этого исключения
     *     «убрать текст в видео» штрафовало бы собственный ответ.
     *
     * Роль без типа («объект», «дорожки») не участвует: штраф за разворот
     * ставится только там, где оба конца — известные типы. Незнакомое слово
     * ничего не утверждает, и наказывать по нему нельзя.
     * ------------------------------------------------------------------ */
    // Звук разложен на три типа: «голос» — это речь, «песня» — музыка, а
    // «аудио», «запись», «mp3» — просто звук. Одним типом они были до
    // 23.09.2026, и «сгенерировать голос из текста» отвечало генератором
    // песен, а «текст в песню» — озвучкой: для «звука» это одна и та же пара.
    var MEDIA_TYPES = {
        audio: 'аудио аудиофаил аудиозапись аудиотрек звук звуковой звуковую запись записи аудиокнига аудиокнигу подкаст рингтон mp3 мр3 мп3 wav flac m4a ogg aac opus wma aiff ' +
            'audio audios sound sounds mp3s audiobook podcast ringtone ' +
            'sonido sonidos pista som sons faixa son sons piste tonspur ' +
            'аудіо звуку ' +
            'صوت تسجيل',
        music: 'музыка музыку музыки песня песню песни песен трек мелодия мелодию фонограмма минусовка ' +
            'music song songs track tracks tune melody musica musicas cancion canciones ' +
            'musique chanson chansons musik lied lieder lagu ' +
            'музику музика пісню пісня пісні ' +
            'موسيقى أغنية',
        speech: 'كلام мовлення речь речи голос голоса голосом озвучка озвучку диктовка диктовку speech voice voices voz voces fala voix parole sprache stimme ucapan мова голосу',
        text: 'نص نصوص текст текста тексту слова транскрипт транскрипция стенограмма text texts transcript texto textos letra texte textes teks текстом',
        subs: 'субтитри субтитры субтитров титры subtitle subtitles caption captions subtitulo subtitulos legenda legendas sous-titres untertitel srt vtt',
        video: 'فيديو відео відеоролик видео видеоролик видеороликов ролик ролика роликов клип клипа клипов слаидшоу слайдшоу фильм кино mp4 мп4 mov avi mkv webm reels shorts ' +
            'video videos clip movie film slideshow vidéo vidéos pelicula filme',
        image: 'صورة صور зображення картинку світлину фото фотка фотку фоток фотография фотографию фотографии фотографий картинка картинку картинки картинок изображение изображения изображений снимок снимка рисунок скриншот jpg jpeg png webp heic ' +
            'image images photo photos picture pictures pic pics img screenshot imagen imagenes foto fotos imagem imagens bild bilder gambar',
        gif: 'gif гиф гифка гифку гифки gifs',
        notes: 'ноти ноты нот партитура партитуру партитуры табы sheet score notes partitura partituras notas partition partitions noten',
        midi: 'midi миди',
        pdf: 'pdf пдф pdfs',
        doc: 'word docx doc ворд вордовский документ документа',
        model3d: '3d stl obj 3д'
    };
    // Семья типа: речь и музыка — это звук, MIDI — ноты, субтитры — текст.
    var TYPE_FAMILY = { speech: 'audio', music: 'audio', midi: 'notes', subs: 'text' };
    function family(t) { return TYPE_FAMILY[t] || t; }
    // Тот же носитель: для прикрепления («фото на видео») и для «названия про
    // другой материал» различать голос и музыку не нужно.
    function sameMedium(a, b) { return !!a && !!b && family(a) === family(b); }

    // Слова-маркеры ПО ЛОКАЛЯМ. Общий на все языки список не годится: «a» —
    // это «в» по-испански и артикль по-английски, «in» — «в» по-немецки и
    // «внутри» по-английски, «en» — «в» по-французски и «внутри» по-испански.
    // Английские маркеры добавляются к каждой локали: «mp3 to wav» набирают
    // на любой. Все списки проходят через normalize() — как и запрос.
    //   fwd    — «X в Y»: вход слева, выход справа;
    //   back   — «Y из X»: выход слева, вход справа;
    //   weak   — «Y de X»: то же, но только после глагола извлечения — само
    //            «de» значит и «из», и «чей» («voz de video» — голос ролика);
    //   open   — «из X в Y»: открывает вход, закрывает его ближайший fwd;
    //   attach — «X на Y»: прикрепление, результат — Y.
    var DIR_LOCALE = {
        ru: { fwd: 'в во', back: 'из изо', open: 'из изо с со от', attach: 'на к ко' },
        uk: { fwd: 'в у', back: 'з із зі', open: 'з із зі від', attach: 'на до' },
        en: { fwd: 'to into', back: 'from', open: 'from', attach: 'on onto over' },
        es: { fwd: 'a', back: 'desde', weak: 'de del', open: 'de desde', attach: 'sobre' },
        pt: { fwd: 'para em', back: 'desde', weak: 'de do da dos das', open: 'de desde do da', attach: 'sobre' },
        fr: { fwd: 'en vers', back: 'depuis', weak: 'de du des', open: 'de depuis du des', attach: 'sur' },
        de: { fwd: 'zu in nach', back: 'aus', weak: 'von', open: 'von aus', attach: 'auf' },
        id: { fwd: 'ke jadi menjadi', back: 'dari', open: 'dari', attach: '' },
        ar: { fwd: 'الى إلى', back: 'من', open: 'من', attach: 'على' },
        ko: { fwd: '', back: '', open: '', attach: '' }
    };
    // Глаголы, превращающие фразу в прикрепление, удаление или извлечение.
    var DIR_VERBS = {
        attach: 'добавить добавь добавление добавления наложить наложи наложение вставить вставь вставка поставить прикрепить приложить положить ' +
            'add adding put insert overlay attach place layer ' +
            'anadir agregar poner insertar colocar adicionar inserir ajouter mettre inserer hinzufugen einfugen tambah tambahkan додати накласти',
        remove: 'убрать убери уберите удалить удали удаление удаления вырезать вырези стереть очистить очистка ' +
            'remove removing delete erase strip quitar eliminar borrar remover tirar retirar supprimer enlever effacer entfernen loschen hapus прибрати видалити',
        extract: 'извлечь извлечение вытащить достать получить extract get rip extraer extraccion sacar obtener extrair extracao extraire extrahieren ambil ekstrak витягти',
        // «Распознать речь в видео», «найти лицо на фото»: предлог здесь —
        // ГДЕ искать, а не во что превратить. В описаниях так пишут постоянно,
        // и без этого класса «Субтитры к видео» объявляли о себе «речь → видео».
        locate: 'распознать распознает распознает распознавание найти наити наидет искать поиск определить определяет обнаружить обнаруживает отследить ' +
            'recognize recognise detect detects find finds search identify locate track reconocer detectar buscar encontrar identificar reconhecer detectar encontrar ' +
            'reconnaitre detecter trouver chercher erkennen finden suchen deteksi temukan розпізнати знайти'
    };
    function wordSet(str) {
        var s = Object.create(null), parts = String(str || '').split(/\s+/);
        for (var i = 0; i < parts.length; i++) if (parts[i]) s[normalize(parts[i])] = 1;
        return s;
    }
    var _dirCache = {};
    function dirSets(locale) {
        var key = DIR_LOCALE[locale] ? locale : 'en';
        if (_dirCache[key]) return _dirCache[key];
        var L = DIR_LOCALE[key], E = DIR_LOCALE.en, S = {};
        ['fwd', 'back', 'weak', 'open', 'attach'].forEach(function (k) {
            S[k] = wordSet((L[k] || '') + (key === 'en' ? '' : ' ' + (E[k] || '')));
        });
        // Глаголы сравниваем по основе: «убрать», «уберу», «удаления».
        ['attach', 'remove', 'extract', 'locate'].forEach(function (k) {
            var set = wordSet(DIR_VERBS[k]), st = Object.create(null);
            for (var w in set) { st[w] = 1; st[stem(w)] = 1; }
            S['v_' + k] = st;
        });
        S.cyr = key === 'ru' || key === 'uk';
        _dirCache[key] = S;
        return S;
    }

    var _typeMap = null;
    function typeOf(tok) {
        if (!_typeMap) {
            _typeMap = Object.create(null);
            for (var t in MEDIA_TYPES) {
                var words = MEDIA_TYPES[t].split(/\s+/);
                for (var i = 0; i < words.length; i++) {
                    if (!words[i]) continue;
                    var w = normalize(words[i]);
                    _typeMap[w] = t;
                    // Основа записывается у каждого слова, а ищется только
                    // у токена от четырёх букв: «son» и «ton» остаются собой,
                    // «картинку» и «картинки» сходятся, а арабское «الصوت»
                    // (с артиклем) находит трёхбуквенное «صوت».
                    _typeMap['~' + stem(w)] = t;
                }
            }
        }
        return _typeMap[tok] || (tok.length >= 4 ? _typeMap['~' + stem(tok)] : undefined) || null;
    }

    function isVerb(tok, set) {
        return !!(set[tok] || set[stem(tok)]);
    }

    /**
     * «в картинке» — это ГДЕ, «в картинку» — КУДА. Предложный падеж у
     * русских существительных почти всегда кончается на «-е» («в песне», «в
     * тексте», «в записи» — на «-и», но она и в винительном «запись»). Без этой
     * проверки «распознать текст в картинке» читалось как «превратить текст в
     * картинку», и распознавание текста получало штраф за разворот. Несклоняемые
     * «видео», «аудио», «фото» падежа не показывают — там остаётся
     * преобразование, как и было задумано.
     */
    function locativeRu(tok) {
        if (!tok || tok.length < 4 || !/[а-я]$/.test(tok)) return false;
        return /(?:е|ах|ях)$/.test(tok);
    }

    /**
     * Первый ТИПИЗИРОВАННЫЙ токен в диапазоне [from, to) в заданную сторону.
     * «Текст песни из аудио»: слева от «из» — «текст песни», голова фразы —
     * «текст», и берём её, а не соседнее слово.
     */
    // Определители, которые стоят между предлогом и существительным и ничего
    // не значат для роли: «from any photo», «в мою песню».
    var ROLE_SKIP = null;
    function roleSkippable(w) {
        if (!ROLE_SKIP) ROLE_SKIP = wordSet('any some my your our this that these those one ' +
            'мой моя мое мои мою моего свой своя свое свои свою своего этот эта это эту ' +
            'mi mis tu tus su sus este esta meu minha seu sua mon ma mes ton ta son sa mein meine dein deine');
        return w.length < 2 || isConnector(w) || ROLE_SKIP[w] === 1;
    }

    /**
     * Тип ближайшего к предлогу существительного в сторону `step`.
     * Служебные слова пропускаются, незнакомых можно перешагнуть не больше
     * `skip` — это прилагательное («в естественную речь»). Дальше искать
     * нельзя: в «Create animated avatars, GIFs and stickers from any photo»
     * слева от «from» стоят «stickers», и тянуться через них к «avatars» —
     * значит приписать инструменту чужую пару.
     */
    function neighborType(toks, i, step, skip) {
        var n = toks.length, seen = 0;
        for (var k = i + step; k >= 0 && k < n; k += step) {
            var w = toks[k], t = typeOf(w);
            if (t) return t;
            if (roleSkippable(w)) continue;
            if (++seen > skip) return null;
        }
        return null;
    }

    /**
     * Голова фразы слева от предлога в НАЗВАНИИ инструмента: первое слово с
     * типом от начала названия. «Текст песни из аудио» — это «текст», а не
     * соседние «песни»; «Извлечь аудио из видео» — «аудио».
     */
    function headType(toks, i) {
        for (var k = 0; k < i; k++) {
            var t = typeOf(toks[k]);
            if (t) return t;
        }
        return null;
    }

    /**
     * Роли в последовательности токенов. `mode` — откуда токены:
     *   'name'  — название: короткое и построенное, вход и выход берутся
     *             головой фразы;
     *   'desc'  — описание: предложения, соседи предлога строго ближайшие;
     *   'kw'    — ключевые слова: подряд склеены сотни фраз, только соседи;
     *   'query' — запрос: ближайшие соседи плюс свободный порядок слов
     *             («создать из текста видео»).
     */
    function extractRoles(toks, mode, locale) {
        var S = dirSets(locale);
        var conv = [], dest = [];
        var n = toks.length;
        var leftSkip = mode === 'query' ? 1 : 0, rightSkip = mode === 'kw' ? 0 : 1;
        for (var i = 0; i < n; i++) {
            var w = toks[i];
            var isFwd = S.fwd[w] === 1, isBack = S.back[w] === 1, isWeak = S.weak[w] === 1, isAttach = S.attach[w] === 1;
            if (!isFwd && !isBack && !isWeak && !isAttach) continue;

            // Глагол перед конструкцией решает, что это: преобразование,
            // прикрепление, удаление, извлечение или поиск.
            var lo = Math.max(0, i - 5), verb = '';
            for (var v = i - 1; v >= lo; v--) {
                if (isVerb(toks[v], S.v_remove)) { verb = 'remove'; break; }
                if (isVerb(toks[v], S.v_attach)) { verb = 'attach'; break; }
                if (isVerb(toks[v], S.v_extract)) { verb = 'extract'; break; }
                if (isVerb(toks[v], S.v_locate)) { verb = 'locate'; break; }
            }
            if (verb === 'remove') continue;
            if (verb === 'locate' && (isFwd || isAttach)) continue;
            if (isWeak && verb !== 'extract') continue;
            if (S.cyr && isFwd && locativeRu(toks[i + 1])) continue;

            var left = mode === 'name' ? headType(toks, i) : neighborType(toks, i, -1, leftSkip);
            var right = neighborType(toks, i, 1, rightSkip);

            if (isAttach || (verb === 'attach' && isFwd)) {
                // [что прикрепляют, к чему]. Первое может быть неизвестно —
                // «наложить на видео», — второе обязано быть типом.
                if (right) dest.push([left, right]);
                continue;
            }
            if (isFwd) {
                // «из X в Y» и «с X на Y»: вход — после открывающего маркера.
                var opened = null;
                for (var o = i - 1; o >= Math.max(0, i - 5); o--) {
                    if (S.open[toks[o]] === 1) { opened = neighborType(toks, o, 1, 1); break; }
                }
                if ((opened || left) && right) conv.push([opened || left, right]);
            } else if (left && right) {
                conv.push([right, left]);
            } else if (!left && right && mode === 'query') {
                // Свободный порядок слов: «создать из текста видео» — выход
                // стоит ПОСЛЕ входа. Берём следующий тип справа.
                for (var r = i + 2; r < Math.min(n, i + 5); r++) {
                    var t2 = typeOf(toks[r]);
                    if (t2 && t2 !== right) { conv.push([right, t2]); break; }
                }
            }
        }
        return { conv: conv, dest: dest };
    }

    function mergeRoles(into, r) {
        for (var i = 0; i < r.conv.length; i++) into.conv.push(r.conv[i]);
        for (var j = 0; j < r.dest.length; j++) into.dest.push(r.dest[j]);
    }

    // Инструмент, который берёт звук, возьмёт и видео: звуковая дорожка
    // вынимается из ролика сама (так устроены все аудио-тулы с аплоадером) —
    // «видео в текст» законно отвечает «Речь в текст». Но только когда
    // человек хочет из звука ЧТО-ТО ДРУГОЕ: «видео в аудио» — это и есть
    // извлечение звука, и инструмент «звук → звук» на него не ответ.
    // Вход: общий тип принимает частный и наоборот («голос в текст» берёт
    // «Аудио в текст»; «mp3 в текст» — «Речь в текст»), но два частных между
    // собой — нет: «песню в текст» не ответ распознаванию речи, для неё есть
    // «Текст песни из аудио». MIDI и ноты, субтитры и текст — одно и то же.
    // ⚠️ Кроме «видео в видео» («mp4 в avi», «mov в mp4»): звуковой инструмент
    // возьмёт из ролика только дорожку и соберёт НОВОЕ видео, выбросив
    // картинку. «Создать видео из аудио» стоял первым на «перевод из МР-4 в
    // AVI» (письмо 28.09.2026) — роль засчитывала ему целую пару.
    function inputCompatible(qIn, dIn, qOut) {
        if (!qIn || !dIn) return false;
        if (qIn === dIn) return true;
        var fq = family(qIn), fd = family(dIn);
        if (fq === fd && (qIn === fq || dIn === fd || fq !== 'audio')) return true;
        return qIn === 'video' && fd === 'audio' && family(qOut) !== 'audio' && qOut !== 'video';
    }
    // Выход асимметричен: «текст в аудио» примет и озвучку, и песню, а
    // «текст в голос» — только озвучку. Просили частное — общее не годится.
    function outputCompatible(qOut, dOut) {
        if (!qOut || !dOut) return false;
        if (qOut === dOut) return true;
        var fq = family(qOut), fd = family(dOut);
        if (fq !== fd) return false;
        return fq !== 'audio' || qOut === 'audio';
    }

    // Сколько пар инструмента совпало с запросом целиком.
    function convFull(q, part) {
        var hits = 0;
        for (var j = 0; j < part.conv.length; j++) {
            for (var i = 0; i < q.conv.length; i++) {
                if (inputCompatible(q.conv[i][0], part.conv[j][0], q.conv[i][1])
                    && outputCompatible(q.conv[i][1], part.conv[j][1])) { hits++; break; }
            }
        }
        return hits;
    }
    // Прикрепление совпало, если совпало МЕСТО и не спорит ПРЕДМЕТ: «фото на
    // видео» — не «музыка к видео», хотя место одно.
    function destHit(q, part) {
        for (var i = 0; i < q.dest.length; i++) {
            for (var j = 0; j < part.dest.length; j++) {
                var qa = q.dest[i], da = part.dest[j];
                if (!sameMedium(qa[1], da[1])) continue;
                if (qa[0] && da[0] && !sameMedium(qa[0], da[0])) continue;
                return true;
            }
        }
        return false;
    }

    /**
     * Сверка ролей запроса с ролями инструмента. Роли у инструмента двух
     * сортов, и доверие к ним разное:
     *   - СИЛЬНЫЕ — из названия и описания. Это то, что инструмент о себе
     *     заявляет. Если он объявил, что во что превращает, и ни одна пара не
     *     совпала с запросом, это другой инструмент: развёрнутый («аудио в
     *     видео» против «видео в аудио»), не берущий материал человека («mp3 в
     *     ноты» против «ноты в MIDI» — тому нужен скан, а не звук) или
     *     делающий другое из того же.
     *   - СЛАБЫЕ — из ключевых слов. Там подряд склеены сотни фраз, в том числе
     *     чужие: у «Убрать звук из видео» в ключевых словах стоит «extract audio
     *     from video», у «Нот в MIDI» — «sheet music to midi», где сосед
     *     предлога «music» читается как звук. Слабая пара подсказывает, но не
     *     наказывает и не спорит с сильной.
     * Возвращает { cov: -1|0|1, mul: множитель счёта }.
     */
    function compareRoles(q, d) {
        var cov = 0, mul = 1, full = false;
        if (q.conv.length) {
            // Прикрепление в названии или описании («Текст и знак на видео»)
            // — тоже заявление о себе: результат у такого инструмента — то,
            // к чему прикрепляют. На «видео в текст» он не ответ, хотя слова
            // «видео» и «текст» у него есть оба.
            // Слабая пара, повторённая в ключевых словах несколько раз, — уже
            // не шум, а суть инструмента, просто не вынесенная в название:
            // «Slideshow Maker» о фото и видео молчит, а его ключевые слова
            // шесть раз говорят «photos to video». Одиночная — подсказка.
            var sHas = d.s.conv.length > 0 || d.s.dest.length > 0, sFull = convFull(q, d.s) > 0;
            var wHits = convFull(q, d.w);
            if (sFull) { cov += 1; mul *= 1.6; full = true; }
            else if (wHits >= 2) { cov += 1; mul *= 1.4; full = true; }
            else if (sHas && !wHits) { cov -= 1; mul *= 0.6; }
            else if (wHits) mul *= 1.15;
            else if (d.w.conv.length) mul *= 0.85;
        }
        if (q.dest.length) {
            if (d.s.dest.length && destHit(q, d.s)) { cov += 1; mul *= 1.35; }
            else if (d.w.dest.length && destHit(q, d.w)) mul *= 1.15;
            else if (d.s.dest.length) mul *= 0.75;
        }
        return { cov: cov, mul: mul, full: full };
    }

    /* Служебные слова: предлоги, союзы, артикли. Вес в покрытии и в счёте
     * они давать не должны: «из аудио в текст» засчитывала «из» как значимое
     * слово, и «Текст песни ИЗ аудио» получала покрытие 3 против 2 у «Аудио в
     * текст» — единица за предлог перебивала точное название. Смысл предлога
     * забирают роли выше; фразовый бонус видит их по-прежнему. */
    var CONNECTORS = 'из изо на по для от до при про без под над за об со ко и или а но ' +
        'with from into onto of for and or the to in on by at an ' +
        'de del la las los el en con para por una uno un y o al ' +
        'do da dos das em no na com um uma e ou ' +
        'du des le les une sur dans avec pour par et ou au aux ' +
        'zu von aus mit fur und der die das den dem ein eine im am ' +
        'ke dari di dan untuk yang dengan ' +
        'із від та або ' +
        'من الي في على';
    var _connSet = null;
    function isConnector(w) {
        if (!_connSet) {
            _connSet = Object.create(null);
            var parts = CONNECTORS.split(/\s+/);
            for (var i = 0; i < parts.length; i++) if (parts[i]) _connSet[normalize(parts[i])] = 1;
        }
        return _connSet[w] === 1;
    }

    /* ------------------------------------------------------------------ *
     * Подготовка индекса
     * ------------------------------------------------------------------ */

    // Вес поля. name важнее всего, intro — самый шумный (это SEO-текст).
    // `tk` — фразы-задачи («убрать голос из песни», «mp4 в mp3»): словарь
    // resources/search/tasks/{locale}.json и выученное из журнала помощника
    // (SearchVocabulary). Легче ключевых слов намеренно: фразы сгенерированы
    // на двести инструментов разом и щедро раздают общие глаголы («создать»,
    // «записать»), и на коротком запросе они не должны спорить с тем, что
    // подобрал человек. Замер 23.09.2026: в весе ключевых слов «создать
    // песню» отдавало рингтон, «кавер» — студию записи.
    var FIELDS = [
        { key: 'name', w: 1.0 },
        { key: 'kw', w: 0.72 },
        { key: 'tk', w: 0.6 },
        { key: 'desc', w: 0.5 },
        { key: 'intro', w: 0.22 }
    ];

    // В RU-индексе ключевые слова лежат вперемешку со всеми локалями (хангыль,
    // арабица, CJK). Для русского поиска это мёртвый вес и перекос IDF.
    var NON_LATIN_CYR = /[؀-ۿऀ-ॿᄀ-ᇿ぀-ヿ㐀-鿿가-힯]/;

    // На /search движок зовут двое — хедер-дропдаун и сама страница — с одним и
    // тем же массивом. Готовим индекс один раз на массив: это ~570 документов со
    // стеммингом каждого поля, делать это дважды незачем.
    var _prepCache = typeof WeakMap === 'function' ? new WeakMap() : null;

    /**
     * Слова УДАЛЕНИЯ для правила «убрать X» в search() — глаголы и
     * существительные («удаление», «removal», «eliminación»): инструмент часто
     * назван существительным («Word removal from a song»). Свой список, а не
     * глаголы ролей (DIR_VERBS.remove): там стоит «вырезать», а «вырезать звук
     * из видео» по-русски чаще значит «извлечь» — с ним правило топило
     * «Извлечь аудио из видео» (замер по живым кликам 29.09.2026).
     */
    var REMOVE_WORDS = 'убрать уберите убери удалить удали удаление удаления удалитель стереть сотри очистить очистка избавиться ' +
        'прибрати прибери видалити видали видалення стерти очистити очищення ' +
        'remove removal remover removing delete deletion erase eraser ' +
        'quitar eliminar eliminacion eliminador borrar borrador remover ' +
        'remocao removedor apagar apagador retirar ' +
        'supprimer suppression enlever effacer effaceur ' +
        'entfernen entferner entfernung loschen ' +
        'hapus menghapus penghapus penghapusan ' +
        '제거 지우기 지우개 삭제 없애기 ' +
        'إزالة ازالة حذف مسح';
    var _rmSet = null;
    function removeWords() {
        if (_rmSet) return _rmSet;
        var set = wordSet(REMOVE_WORDS), st = Object.create(null);
        for (var w in set) { st[w] = 1; st[stem(w)] = 1; }
        return (_rmSet = st);
    }

    function mentionsRemoval(doc, set) {
        var keys = ['name', 'desc', 'tk'];
        for (var f = 0; f < keys.length; f++) {
            // Основы уже посчитаны при разборе поля — стеммить заново незачем.
            var e = doc.f[keys[f]];
            for (var k = 0; k < e.tok.length; k++) if (set[e.tok[k]] || set[e.stem[k]]) return true;
        }
        return false;
    }

    function prepare(items, locale) {
        locale = locale || 'en';
        if (_prepCache) {
            var hit = _prepCache.get(items);
            if (hit && hit.locale === locale) return hit;
        }
        var built = build(items, locale);
        if (_prepCache) _prepCache.set(items, built);
        return built;
    }

    function build(items, locale) {
        var ruLike = locale === 'ru';
        var docs = new Array(items.length);
        var df = Object.create(null);     // document frequency по основам
        // То же без intro: слово, которое живёт только в SEO-прозе, покрытия
        // не даёт (см. search()), и склеивать запрос ради него нельзя.
        var dfStrong = Object.create(null);
        var rmSet = removeWords();
        var i, f, k;

        for (i = 0; i < items.length; i++) {
            var t = items[i];
            var doc = { t: t, f: {}, nameNorm: normalize(t.name), pop: +(t.p || 0), boost: +(t.b || 0) };
            var seen = Object.create(null), tkPhrases = null;

            for (f = 0; f < FIELDS.length; f++) {
                var raw = t[FIELDS[f].key] || '';
                if (ruLike && FIELDS[f].key === 'kw' && NON_LATIN_CYR.test(raw)) {
                    raw = raw.replace(NON_LATIN_CYR, ' ');
                    raw = raw.replace(new RegExp(NON_LATIN_CYR.source, 'g'), ' ');
                }
                // 2 — основа из поля, дающего покрытие; 1 — только из intro.
                var mark = FIELDS[f].key !== 'intro' ? 2 : 1;
                // Фразы-задачи (`tk`) приходят строками через перевод строки.
                // Слова у них общие с полем, но ФРАЗОВЫЕ совпадения обязаны
                // оставаться внутри одной фразы: иначе хвост одной и начало
                // следующей («…сделать громче | тихая запись…») складывались в
                // «фразу», которой ни у кого нет, и получали бонус целой фразы.
                var toks, starts = null;
                if (FIELDS[f].key === 'tk' && raw.indexOf('\n') >= 0) {
                    // Одна нормализация на поле, потом строки: normalize()
                    // перевод строки не трогает.
                    var lines = normalize(raw).split('\n');
                    toks = []; starts = []; tkPhrases = [];
                    for (var ln = 0; ln < lines.length; ln++) {
                        var lt = lines[ln].split(SPLIT_RE), phraseAt = toks.length;
                        for (k = 0; k < lt.length; k++) if (lt[k]) toks.push(lt[k]);
                        if (toks.length > phraseAt) { starts[phraseAt] = 1; tkPhrases.push(toks.slice(phraseAt)); }
                    }
                } else {
                    toks = tokenize(raw);
                    if (FIELDS[f].key === 'tk') tkPhrases = [toks];
                }
                var entry = { tok: toks, stem: new Array(toks.length) };
                for (k = 0; k < toks.length; k++) {
                    var st = stem(toks[k]);
                    entry.stem[k] = st;
                    if ((seen[st] || 0) < mark) seen[st] = mark;
                }
                // Основы одной строкой — по ней ищется совпадение фразы целиком
                // («убрать шум» стоит рядом в ключевых словах Шумоочистки, а в
                // «Убрать звук из видео» рядом не стоит). Между фразами — знак,
                // которого нет ни в одной основе: игла « a b » через него не
                // перешагнёт.
                if (starts) {
                    var jn = ' ';
                    for (k = 0; k < toks.length; k++) jn += (k && starts[k] ? '| ' : '') + entry.stem[k] + ' ';
                    entry.joined = jn;
                } else {
                    entry.joined = ' ' + entry.stem.join(' ') + ' ';
                }
                doc.f[FIELDS[f].key] = entry;

                /* Обратная сторона составного слова: человек пишет СЛИТНО
                 * («аудиомикшер»), а инструмент называется через пробел
                 * («Аудио микшер»). Склейка запроса тут не поможет — склеивать
                 * нечего, — поэтому склеенные пары соседних слов запоминаются
                 * со стороны ДОКУМЕНТА. Живой случай: «аудиомикшер» не находил
                 * «Аудио микшер» вовсе (единственное вхождение этой строки во
                 * всём индексе — SEO-проза чужого инструмента, а проза сама по
                 * себе покрытия не даёт).
                 *
                 * Только название и описание: ключевые слова — это сотни
                 * токенов на инструмент, и пары из них раздули бы индекс и
                 * притащили случайные склейки. */
                if (FIELDS[f].key === 'name' || FIELDS[f].key === 'desc') {
                    for (k = 0; k + 1 < toks.length; k++) {
                        var pairRaw = toks[k] + toks[k + 1];
                        if (pairRaw.length < 6) continue;
                        if (!doc.glue) doc.glue = Object.create(null);
                        if (doc.glue[pairRaw] === undefined) doc.glue[pairRaw] = f;
                        var pairStem = stem(pairRaw);
                        if (doc.glue[pairStem] === undefined) doc.glue[pairStem] = f;
                    }
                }
            }
            for (var s in seen) {
                df[s] = (df[s] || 0) + 1;
                if (seen[s] === 2) dfStrong[s] = (dfStrong[s] || 0) + 1;
            }

            // Роли «вход → выход» (см. «Направление»). Название и описание —
            // фразы, в них ищем голову на несколько слов; ключевые слова —
            // склейка сотен фраз, там только соседи маркера. Фразы-задачи —
            // каждая сама по себе: сосед предлога из чужой фразы — не сосед.
            var strong = { conv: [], dest: [] };
            mergeRoles(strong, extractRoles(doc.f.name.tok, 'name', locale));
            mergeRoles(strong, extractRoles(doc.f.desc.tok, 'desc', locale));
            var weakR = extractRoles(doc.f.kw.tok, 'kw', locale);
            for (var tp = 0; tkPhrases && tp < tkPhrases.length; tp++) mergeRoles(weakR, extractRoles(tkPhrases[tp], 'kw', locale));
            if (strong.conv.length || strong.dest.length || weakR.conv.length || weakR.dest.length) {
                doc.roles = { s: strong, w: weakR };
            }

            // Инструмент сам говорит, что УДАЛЯЕТ: глагол удаления в названии,
            // описании или фразах-задачах (ключевые слова не в счёт — там
            // вперемешку чужое: «удалить exif» у «Водяного знака»). См.
            // «убрать X» в search().
            if (mentionsRemoval(doc, rmSet)) doc.removes = true;

            // Типы данных в НАЗВАНИИ: «Убрать текст из видео» — про видео,
            // «Субтитры к видео» — тоже. См. «несовпадение носителя» в search().
            var nt = null;
            for (k = 0; k < doc.f.name.tok.length; k++) {
                var ty = typeOf(doc.f.name.tok[k]);
                if (ty) (nt || (nt = [])).push(ty);
            }
            if (nt) doc.nameTypes = nt;

            docs[i] = doc;
        }

        return { docs: docs, df: df, dfStrong: dfStrong, n: items.length, locale: locale };
    }

    /**
     * Починка опечатки в ЗАПРОСЕ — до скоринга, а не после.
     *
     * Добор по опечаткам ниже (`out.length < 5`) спасает только пустую выдачу.
     * Но опечатка в ОДНОМ слове двусловного запроса выдачу не опустошает:
     * второе слово набирает десяток документов с покрытием 1, порог `< 5` не
     * срабатывает, и человек получает список мимо. Замерено на живом промахе
     * 09.08.2026 («удленнить вокализ» → 16 карточек, ни одной по делу, человек
     * ушёл в заявку) — заявка #26.
     *
     * Чиним ТОЛЬКО слово, до которого индекс не дотягивается НИКАК: нет такой
     * основы, ни одна известная основа не «близкая», и оно не начало ни одного
     * известного слова. Все три проверки обязательны, и каждая куплена
     * промахом стенда:
     *   - без проверки на близкую основу «вокализ» (термин, которого у нас
     *     нет) переписывался в «вокал» и уводил человека в удаление вокала —
     *     подмена смысла, а не починка опечатки;
     *   - без проверки на префикс живой ввод «стенографи» переписывался в
     *     «стенограмма» ДО того, как сработает префиксный матч, и «Стеганография»
     *     из выдачи исчезала.
     * У известного слова опечатки нет по определению, и подменять его нельзя —
     * иначе редкий точный термин уедет в частый похожий.
     */
    /**
     * Индекс для починки опечаток. Строится ЛЕНИВО — при первом запросе, где
     * встретилось незнакомое слово: на большинстве сессий он не нужен вовсе, а
     * стоит 25 мс поверх prepare() (замерено на 644 инструментах).
     *
     * Словарь — целые слова из названия/описания/ключевых слов; intro не берём
     * по той же причине, что и добор по опечаткам: это самый длинный текст, и
     * он затащил бы в словарь случайную прозу.
     *
     * Корзины по ДВУМ ПЕРВЫМ буквам, а не сплошной список: починка и проверка
     * достижимости идут на каждое неизвестное слово, то есть в живом вводе — на
     * каждое нажатие клавиши. Сплошной перебор 14 тысяч слов стоил +48 мс на
     * запрос (замерено), по корзине — доли миллисекунды.
     *
     * Кандидаты в ЗАМЕНУ — по ПЕРВОЙ букве (`repair1`), а не по двум: вторая
     * буква русского слова — это чаще всего безударная гласная первого слога,
     * самое частое место ошибки («дипрессию», «колибровка», «римонт»). По двум
     * буквам такая опечатка не чинилась НИКОГДА: «тест на дипрессию» отдавал
     * IQ-тест (письмо 28.09.2026). Корзина по одной букве вдесятеро крупнее,
     * но проходится только на незнакомом слове и отсевом по длине.
     */
    function ensureRepairIndex(prep) {
        if (prep.vocab) return;
        var byPre = Object.create(null), seen = Object.create(null), i, f, k;
        // Кандидаты в ЗАМЕНУ — отдельно. У латинских локалей ключевые слова —
        // общий пул на все языки (stripForeignScripts оставляет латиницу
        // везде), и починка брала оттуда чужое слово: английское «cartoon»
        // переписывалось в португальское «cartoes» из ключевых слов
        // «Смешивания цветов», и помощник на главной ставил его первым
        // (23.09.2026). Заменять можно только словом из названия или описания
        // этой локали; достижимость и разрез слипшегося слова по-прежнему
        // смотрят на весь словарь.
        var latin = !(prep.locale === 'ru' || prep.locale === 'uk' || prep.locale === 'ko' || prep.locale === 'ar');
        var repairPre = latin ? Object.create(null) : null, repairSeen = Object.create(null);
        for (i = 0; i < prep.docs.length; i++) {
            for (f = 0; f < FIELDS.length - 1; f++) {
                var tok = prep.docs[i].f[FIELDS[f].key].tok;
                var own = FIELDS[f].key !== 'kw';
                for (k = 0; k < tok.length; k++) {
                    var w = tok[k];
                    if (w.length < 4) continue;
                    if (repairPre && own && !repairSeen[w]) {
                        repairSeen[w] = 1;
                        (repairPre[w.slice(0, 2)] || (repairPre[w.slice(0, 2)] = [])).push(w);
                    }
                    if (seen[w]) continue;
                    seen[w] = 1;
                    (byPre[w.slice(0, 2)] || (byPre[w.slice(0, 2)] = [])).push(w);
                }
            }
        }
        prep.repairVocab = repairPre;
        var from = repairPre || byPre, first = Object.create(null);
        for (var pk in from) {
            var bucket = first[pk[0]] || (first[pk[0]] = []);
            for (k = 0; k < from[pk].length; k++) bucket.push(from[pk][k]);
        }
        prep.repair1 = first;
        // «Тело» основы — она без последней буквы. stemsNear() считает близкими
        // основы одной длины, различающиеся только последней буквой, поэтому
        // достижимость по близкой основе — проверка наличия тела в множестве, а
        // не перебор всех основ.
        var bodies = Object.create(null);
        for (var s in prep.df) if (s.length >= 4) bodies[s.length + ':' + s.slice(0, -1)] = 1;
        prep.vocab = byPre;
        prep.stemBodies = bodies;
    }

    /**
     * Дотягивается ли обычный матчинг до слова без починки: близкая основа
     * (`stemsNear` — тот же класс, что даёт Q_NEAR) или слово-продолжение
     * (префиксный матч живого ввода). Если да — чинить нечего.
     */
    function reachable(word, wordStem, prep) {
        if (wordStem.length >= 4
            && prep.stemBodies[wordStem.length + ':' + wordStem.slice(0, -1)]) return true;
        var list = prep.vocab[word.slice(0, 2)];
        if (list) {
            for (var i = 0; i < list.length; i++) {
                if (list[i].length > word.length && list[i].lastIndexOf(word, 0) === 0) return true;
            }
        }
        return false;
    }

    var REPAIR_MIN = 0.62;
    function repairQuery(sig, stems, prep) {
        // ТОЛЬКО многословный запрос. У однословного добор по опечаткам ниже и
        // так возвращает целую окрестность («шумаме» → Шумомер), а починка её
        // обнуляет: найденный по исправленному слову документ даёт покрытие 1,
        // после чего отсечка хвоста выбрасывает всё с покрытием 0. Замерено на
        // «стенографи»: 34 карточки со Стеганографией второй превращались в
        // одну. Портится именно многословный запрос — там второе слово набирает
        // документы с покрытием 1, полное покрытие недостижимо, и правильный
        // ответ не всплывает ничем.
        if (sig.length < 2) return null;
        var fixed = null, indexed = false;
        for (var i = 0; i < sig.length; i++) {
            var w = sig[i];
            if (w.length < 5 || prep.df[stems[i]]) continue;
            // Слово, у которого ЕСТЬ синоним, — не опечатка: каталог его не
            // знает, а словарь знает, что оно значит. Починка об этом не
            // спрашивала и переписывала такое слово в похожее по буквам ДО
            // расширения синонимами, то есть отменяла словарь ровно там, где
            // он и нужен: «перекинуть файл» → «перевернуть файл» → «Повернуть
            // PDF» (замер 09.09.2026). Уязвимы были 19 ключей из 69 на
            // RU-индексе — не только новые, но и давние «видос», «склеить»,
            // «изолировать», «сделать»: их основы в каталоге тоже не
            // встречаются.
            if (synonymDict(prep.locale)[stems[i]] || measureVerb(stems, i, prep.locale)) continue;
            // Индекс строим только когда незнакомое слово реально встретилось.
            if (!indexed) { ensureRepairIndex(prep); indexed = true; }
            if (reachable(w, stems[i], prep)) continue;
            var best = '', bestScore = REPAIR_MIN, list = prep.repair1[w[0]];
            for (var j = 0; list && j < list.length; j++) {
                // Опечатка длину почти не меняет — отсев до дорогой биграммы.
                if (Math.abs(list[j].length - w.length) > 2) continue;
                var sc = bigram(w, list[j]);
                if (sc > bestScore) { bestScore = sc; best = list[j]; }
            }
            // Родственное слово — НЕ опечатка. «вокализ» → «вокал» проходит по
            // похожести (0.8 — выше, чем у настоящей опечатки «удленнить» →
            // «удлинить», 0.67, так что порогом эти случаи не развести), но это
            // подмена смысла: человек спросил про вокализ, а его увели в
            // удаление вокала. Признак родства — одно слово начинает другое;
            // такие связи обслуживают стеммер и добор по опечаткам, не мы.
            if (best && (best.lastIndexOf(w, 0) === 0 || w.lastIndexOf(best, 0) === 0)) best = '';
            // Замена обязана быть словом, которое индекс ЗНАЕТ как основу —
            // иначе мы меняем одно неизвестное слово на другое.
            if (best && prep.df[stem(best)]) {
                sig[i] = best;
                stems[i] = stem(best);
                (fixed || (fixed = [])).push({ from: w, to: best });
            }
        }
        return fixed;
    }

    function idf(prep, s) {
        var d = prep.df[s] || 0;
        if (!d) return 1.6;                       // неизвестное слово считаем редким
        return Math.max(0.25, Math.log(prep.n / d));
    }

    /* ------------------------------------------------------------------ *
     * Матчинг одного слова запроса по одному документу
     * ------------------------------------------------------------------ */

    // Качество совпадения — во сколько раз слово «попало» (1.0 = точное).
    var Q_EXACT = 1.0, Q_STEM = 0.92, Q_PREFIX = 0.82, Q_COMPOUND = 0.7, Q_NEAR = 0.55, Q_INSIDE = 0.34;

    function matchWord(doc, qw, qs, allowPrefix) {
        var best = 0, bestField = -1;
        for (var f = 0; f < FIELDS.length; f++) {
            var fw = FIELDS[f].w;
            if (fw * Q_EXACT <= best) continue;   // это поле уже не побьёт результат
            var e = doc.f[FIELDS[f].key], toks = e.tok, stems = e.stem, q = 0;
            for (var i = 0; i < toks.length; i++) {
                var tk = toks[i], st = stems[i], cur = 0;
                if (tk === qw) cur = Q_EXACT;
                else if (st === qs) cur = Q_STEM;
                else if (allowPrefix && tk.length > qw.length && tk.lastIndexOf(qw, 0) === 0) cur = Q_PREFIX;
                // Сложное слово: «автосубтитры» ⊃ «субтитры», «видеоплеер» ⊃
                // «плеер». Если запрос занимает больше половины токена, это то же
                // слово с приставкой, а не случайное вхождение подстроки.
                else if (qw.length >= 5 && tk.indexOf(qw) > 0 && qw.length / tk.length >= 0.55) cur = Q_COMPOUND;
                else if (stemsNear(st, qs)) cur = Q_NEAR;
                else if (tk.length > qw.length && qw.length >= 4 && tk.indexOf(qw) > 0) cur = Q_INSIDE;
                if (cur > q) { q = cur; if (q === Q_EXACT) break; }
            }
            if (q * fw > best) { best = q * fw; bestField = f; }
        }
        return { s: best, field: bestField };
    }

    /**
     * Формат файла, набранный кириллицей или разорванный дефисом: «мр3» — это
     * «mp3» кириллическими «м» и «р» (на вид те же буквы, и так печатают, не
     * переключая раскладку), «мп4» — транслит, «3д» — «3d», а «МР-4» и «mp-4»
     * токенайзер режет по дефису, и от формата остаются «мр» и «4». В индексе
     * все форматы латиницей, поэтому ни одна из этих форм не совпадала ни с
     * чем: «мп3», «мр3 в», «в мр4» — пустая выдача в `search_miss` за 90 дней,
     * «перевод из МР-4 в AVI» — пятым «Конвертер видео» (письмо 28.09.2026).
     *
     * Сворачиваем только токен с ЦИФРОЙ: русского слова с цифрой внутри не
     * бывает, а у форматов, которые так ломаются, она есть всегда. Пару «буквы
     * + цифры» склеиваем, только если в буквах две-три литеры: одиночная
     * буква перед числом — это предлог («в 2 раза»), а не «v2». И в обоих
     * случаях — лишь когда получившийся токен индексу ИЗВЕСТЕН.
     *
     * ⚠️ Кириллический токен, который индекс знает САМ («3д», «4к», «мп3» —
     * так они записаны в ключевых словах части инструментов), не заменяется, а
     * получает второе написание (`alt`): слово ищется обоими. Замена теряла
     * эти точные совпадения — «анимация 3д» и «разрешение в 4к» уезжали от
     * своих ответов на замере по живым кликам.
     */
    var CYR_LAT = {
        'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'з': 'z', 'и': 'i', 'к': 'k', 'л': 'l',
        'м': 'm', 'н': 'n', 'о': 'o', 'п': 'p', 'р': 'p', 'с': 'c', 'т': 't', 'у': 'y', 'ф': 'f', 'х': 'x'
    };
    function latinOf(tok) {
        var out = '';
        for (var i = 0; i < tok.length; i++) {
            var ch = tok[i];
            if (ch >= 'а' && ch <= 'я') {
                if (!CYR_LAT[ch]) return '';
                out += CYR_LAT[ch];
            } else out += ch;
        }
        return out;
    }
    /**
     * @returns {{words: string[], alt: (Object|null)}} alt[токен] — его второе
     *   написание; ключ — сам токен, а не номер: дальше запрос теряет обёртку,
     *   склеивается и прореживается, и номера сдвигаются.
     */
    function foldFormats(words, prep) {
        if (!prep || !prep.df) return { words: words, alt: null };
        var out = [], alt = null, i = 0;
        while (i < words.length) {
            var w = words[i], nx = words[i + 1];
            // «мр» + «4», «mp» + «3»: буквы 2–3 литеры и следом одни цифры.
            if (nx && /^[a-zа-я]{2,3}$/.test(w) && /^\d{1,3}$/.test(nx)) {
                var glued = (/[а-я]/.test(w) ? latinOf(w) : w) + nx;
                if (glued.length > nx.length + 1 && prep.df[stem(glued)]) {
                    out.push(glued); i += 2; continue;
                }
            }
            if (/\d/.test(w) && /[а-я]/.test(w)) {
                var lat = latinOf(w);
                if (lat && prep.df[stem(lat)]) {
                    if (prep.df[stem(w)]) {
                        (alt || (alt = Object.create(null)))[w] = lat;
                    } else {
                        out.push(lat); i++; continue;
                    }
                }
            }
            out.push(w);
            i++;
        }
        return { words: out, alt: alt };
    }

    /**
     * Составное слово, написанное РАЗДЕЛЬНО: «слайд шоу» → «слайдшоу».
     *
     * Матчинг умеет обратное (`Q_COMPOUND`: запрос «субтитры» находит токен
     * «автосубтитры»), но раздельный ввод не ловит НИЧЕМ: «слайд» стоит в
     * начале токена, то есть попадает под префиксный матч, а он разрешён
     * только последнему слову; «шоу» — три буквы, короче порога Q_COMPOUND.
     * Замер на живом RU-индексе: «слайд шоу» отдавал ОДНУ карточку
     * («Презентация к уроку нейросетью»), а «слайдшоу» — правильный ответ
     * первым. Тот же промах записан в телеметрии search_miss.
     *
     * Склеиваем только тогда, когда склеенное слово РЕАЛЬНО есть в корпусе
     * (`prep.df`): иначе правило молчит и цена его — один просмотр словаря на
     * пару соседних слов. Пороги по длине отсекают предлоги («из», «с») —
     * приклеивать их к соседу нельзя.
     *
     * ⚠️ И не в SEO-прозе: склейка ЗАМЕНЯЕТ два слова одним, а слово, которое
     * живёт только в intro, покрытия не создаёт. «видео редактор» склеивалось в
     * «видеоредактор» (одно вхождение на весь каталог — проза «Обрезки видео»),
     * и запрос из двух частых слов отдавал одни «похожие по написанию» с
     * «Фоторедактором» первым (письмо 28.09.2026). Поэтому склеенное слово
     * ищется в `dfStrong` — в полях, которые покрытие дают.
     */
    function joinCompounds(words, prep) {
        if (!prep || !prep.df || words.length < 2) return { words: words, parts: null };
        var dfs = prep.dfStrong || prep.df;
        var out = [], parts = [], merged = false, i = 0;
        while (i < words.length) {
            var a = words[i], b = words[i + 1];
            if (b && a.length >= 3 && b.length >= 3 && dfs[stem(a + b)]) {
                out.push(a + b); parts.push([a, b]); i += 2; merged = true; continue;
            }
            out.push(a); parts.push(null); i++;
        }
        return merged ? { words: out, parts: parts } : { words: words, parts: null };
    }

    /**
     * Склеенное слово запроса против РАЗДЕЛЬНОГО написания в документе.
     *
     * Склейка выше — это ПРОЧТЕНИЕ строки, а не утверждение о том, как слово
     * пишется у нас. Пока прочтение было единственным, оно теряло документ,
     * который пишет то же самое через пробел: «аудио микшер» склеивалось в
     * «аудиомикшер» (такой токен есть в ключевых словах ОДНОГО инструмента),
     * после чего «Аудио микшер» — инструмент, чьё НАЗВАНИЕ и есть этот запрос,
     * — не совпадал ни одним словом и не попадал в выдачу вовсе: человек
     * получал «Аудиоплеер», «Тест слуха» и «Аудиокомпас», набранные по
     * похожести написания. Замер по живым парам «запрос → клик»: 12 сессий за
     * 90 дней искали «аудио микшер» и не видели его даже в первой восьмёрке,
     * при том что «микшер аудио» отвечало правильно с первого места. Порядок
     * слов решал, найдётся инструмент или нет.
     *
     * Поэтому склеенный токен ищется ОБОИМИ написаниями: прямым совпадением
     * (`matchWord`) и соседством основ в поле (`joined` построен при
     * индексации, это одна подстрока). Соседство обязано быть в том же
     * порядке, что в запросе, — именно оно отличает составное слово от двух
     * случайных попаданий.
     */
    function matchGlued(doc, pair) {
        var needle = ' ' + stem(pair[0]) + ' ' + stem(pair[1]) + ' ';
        for (var f = 0; f < FIELDS.length; f++) {
            if (doc.f[FIELDS[f].key].joined.indexOf(needle) >= 0) {
                return { s: Q_STEM * FIELDS[f].w, field: f };
            }
        }
        return { s: 0, field: -1 };
    }

    /* ------------------------------------------------------------------ *
     * Поиск
     * ------------------------------------------------------------------ */

    function search(query, prep, opts) {
        opts = opts || {};
        var qNorm = normalize(query);
        if (!qNorm) return [];

        var words = tokenize(qNorm);
        if (!words.length) return [];
        // «мр3», «МР-4», «3д» — формат, набранный кириллицей или через дефис.
        var folded = foldFormats(words, prep);
        words = folded.words;
        var altOf = folded.alt;

        // Разговорную обёртку («как мне …», «я хочу …», «найди мне …») снимаем
        // ЗДЕСЬ, до скоринга, а не в fallback() по пустой выдаче. Замер
        // 2026-08-13 на живом RU-индексе (653 тула, 15 пар «фраза / та же
        // фраза без обёртки») показал, что проблема НЕ в пустой выдаче —
        // выдача непустая всегда, — а в ранжировании: у 5 пар из 15 первое
        // место разное, причём разговорный вариант отвечал мимо. «мне нужно
        // сжать пдф» → «Калькулятор фоновой подсветки», «как узнать скорость
        // интернета» → «Карта ветров». Служебные слова добирают покрытие
        // случайным тулам, и настоящий ответ съезжает на 2–3 место.
        // Запасной путь такое не лечит в принципе: до него не доходит.
        var stripped = stripIntentTokens(words, prep.locale);
        if (stripped) words = stripped;

        // Раздельно набранное составное слово — до фильтра предлогов и до
        // стемминга: «слайд шоу» обязано вести себя как «слайдшоу».
        var glued = joinCompounds(words, prep);
        words = glued.words;
        var gluedParts = glued.parts;

        // Роли читаются по ПОЛНОЙ строке — предлоги здесь и есть смысл.
        var qRoles = words.length > 1 ? extractRoles(words, 'query', prep.locale) : null;
        if (qRoles && !qRoles.conv.length && !qRoles.dest.length) qRoles = null;

        // Однобуквенные слова в многословном запросе — предлоги/союзы («в», «из»,
        // «с»), они только размывают покрытие. `sigParts` едет рядом, потому
        // что «какие из слов склеены» нумеруется ПОСЛЕ этого отсева: сдвиг на
        // один предлог приписал бы склейку чужому слову.
        //
        var sig = words, sigParts = gluedParts;
        if (words.length > 1) {
            sig = []; sigParts = gluedParts ? [] : null;
            for (var si = 0; si < words.length; si++) {
                if (words[si].length < 2) continue;
                sig.push(words[si]);
                if (sigParts) sigParts.push(gluedParts[si]);
            }
            if (!sig.length) { sig = words; sigParts = gluedParts; }
        }

        var stems = sig.map(stem);

        // Двухбуквенные и длиннее служебные слова («из», «для», «from», «para»)
        // остаются в запросе, но не создают покрытия и весят втрое меньше.
        // Покрытие за предлог перебивало точное название: «из аудио в текст»
        // давала «Тексту песни ИЗ аудио» покрытие 3 против 2 у «Аудио в
        // текст». Выкинуть их совсем тоже нельзя — замер на живых запросах:
        // «видео для шор» находил «Видео для Reels и Shorts» именно по «для»,
        // «на вид» — «Текст и знак на видео» по «на». ПОСЛЕДНЕЕ слово служебным
        // не считается никогда: его ещё допечатывают, и «из» бывает началом
        // «изображения», «но» — «нот».
        var isConn = {};
        for (var cn = 0; cn < sig.length - 1; cn++) if (isConnector(sig[cn])) isConn[cn] = 1;

        // Фраза — это соседние ЗНАЧИМЫЕ слова вместе со служебными между ними:
        // «аудио в текст» ищется в документе и как « аудио в текст », и как
        // « аудио текст ». Раньше пары строились по словам от двух букв, и
        // однобуквенный предлог рвал пару: « аудио текст » в названии «Аудио в
        // текст» не находилось, а « из аудио » в «Текст песни из аудио» —
        // находилось, то есть бонус за фразу получал не тот инструмент.
        var phrasePairs = [];
        (function () {
            var content = [];
            for (var ci = 0; ci < words.length; ci++) {
                if (words[ci].length >= 2 && !isConnector(words[ci])) content.push(ci);
            }
            for (var cp = 0; cp + 1 < content.length; cp++) {
                var a = content[cp], b = content[cp + 1];
                var sa = stem(words[a]), sb = stem(words[b]);
                var needles = [' ' + sa + ' ' + sb + ' '];
                if (b - a > 1) {
                    var mid = [];
                    for (var mi = a + 1; mi < b; mi++) mid.push(stem(words[mi]));
                    needles.push(' ' + sa + ' ' + mid.join(' ') + ' ' + sb + ' ');
                }
                phrasePairs.push(needles);
            }
            // Хвост живого ввода: «видео из», «на вид» — последнее слово ещё
            // допечатывают, и оно само по себе ничего не значит, а вместе с
            // соседом это начало фразы из названия («Создать видео из аудио»,
            // «Текст и знак на видео»). Последнее слово — префиксом.
            if (sig.length >= 2) {
                var la = sig[sig.length - 2], lb = sig[sig.length - 1];
                if (isConnector(la) || isConnector(lb)) {
                    phrasePairs.push([' ' + stem(la) + ' ' + stem(lb)]);
                }
            }
        })();

        // Весь запрос одной фразой: « созда песн » и тот же запрос с
        // предлогами (« из аудио в текст »). Нужен ровно тем случаям, где
        // слова запроса разбросаны по чужим фразам инструмента: «создать
        // песню» закрывали и «Создать рингтон» (название) + «рингтон из
        // песни» (фраза словаря), а у «Генератора песен» та же строка стоит
        // целиком. Префикс последнего слова — как везде: его допечатывают.
        var wholeNeedles = [];
        (function () {
            var content = [], all = [];
            for (var wi = 0; wi < words.length; wi++) {
                var st = stem(words[wi]);
                all.push(st);
                if (words[wi].length >= 2 && !isConnector(words[wi])) content.push(st);
            }
            if (content.length >= 2) {
                wholeNeedles.push(' ' + content.join(' ') + ' ');
                if (all.length > content.length) wholeNeedles.push(' ' + all.join(' ') + ' ');
            }
        })();

        // Запрос велит УБРАТЬ («удалить водяной знак», «remove watermark»).
        var qRemove = false, rmVerbs = removeWords();
        for (var qv = 0; qv < sig.length && !qRemove; qv++) qRemove = isVerb(sig[qv], rmVerbs);

        // Типы данных, которые назвал запрос («картинку» — фото, «ролик» — видео).
        var qTypes = [];
        for (var qt = 0; qt < sig.length; qt++) {
            var qty = typeOf(sig[qt]);
            if (qty && qTypes.indexOf(qty) < 0) qTypes.push(qty);
        }
        // Слова ДО починки — их сравнивает добор по опечаткам в конце.
        var raw = sig.slice();
        // Опечатку правим ДО расширения синонимами и до IDF: неизвестное слово
        // иначе получает idf 1.6, становится «значимым» и делает полное
        // покрытие недостижимым для всех — см. repairQuery.
        var repaired = repairQuery(sig, stems, prep);
        var syn = expandQuery(stems, prep.locale);
        var idfs = stems.map(function (s) { return idf(prep, s); });

        // «Значимые» слова запроса — те, что реально сужают выбор. «создать
        // субтитры», «онлайн тест звука»: глагол и «онлайн» есть у сотни
        // инструментов, спрашивают именно про субтитры и звук. Покрытие считаем
        // по ним, иначе инструмент, поймавший только мусорное слово, попадает в
        // тот же тир, что и точный ответ.
        var KEY_IDF = 1.0;
        var keyIdx = [];
        for (var ki = 0; ki < sig.length; ki++) if (idfs[ki] >= KEY_IDF && !isConn[ki]) keyIdx.push(ki);
        if (!keyIdx.length) for (var kc = 0; kc < sig.length; kc++) if (!isConn[kc]) keyIdx.push(kc);
        if (!keyIdx.length) for (var ka = 0; ka < sig.length; ka++) keyIdx.push(ka);
        var isKey = {};
        for (var kk = 0; kk < keyIdx.length; kk++) isKey[keyIdx[kk]] = 1;

        // Насколько запрос вообще различающий. «тест» подходит трём десяткам
        // инструментов одинаково хорошо — там решать должна популярность;
        // «стеганография» указывает на один — там популярность не при чём.
        var avgIdf = 0;
        for (var ai = 0; ai < keyIdx.length; ai++) avgIdf += idfs[keyIdx[ai]];
        avgIdf /= keyIdx.length;
        var popWeight = Math.min(1.1, 2.2 / (1 + avgIdf));

        // Префиксный матч разрешаем только последнему слову — его пользователь
        // ещё дописывает. Иначе «тест» начинает цеплять «тестирование» везде.
        var lastIdx = sig.length - 1;

        var out = [];
        // Типы, из которых сложены роли запроса («голос» → речь, «текст» → текст).
        var qRoleTypes = {};
        if (qRoles) {
            for (var qr = 0; qr < qRoles.conv.length; qr++) { qRoleTypes[qRoles.conv[qr][0]] = 1; qRoleTypes[qRoles.conv[qr][1]] = 1; }
        }

        for (var d = 0; d < prep.docs.length; d++) {
            var doc = prep.docs[d];
            var score = 0, covered = 0, direct = 0, nameHits = 0, introOnly = 0, strong = 0;
            var rc = (qRoles && doc.roles) ? compareRoles(qRoles, doc.roles) : null;
            // Только ПРЕОБРАЗОВАНИЕ закрывает слова-типы: у прикрепления совпало
            // одно место («…на видео»), и засчитывать ему «фото» было бы щедро.
            var roleFull = !!(rc && rc.full);

            for (var i = 0; i < sig.length; i++) {
                var m = matchWord(doc, sig[i], stems[i], i === lastIdx || sig.length === 1);
                // Тот же токен, написанный в документе через пробел.
                if (sigParts && sigParts[i]) {
                    var mg = matchGlued(doc, sigParts[i]);
                    if (mg.s > m.s) m = mg;
                }
                // И наоборот: слитный запрос против раздельного названия.
                if (doc.glue) {
                    var gf = doc.glue[sig[i]];
                    if (gf === undefined) gf = doc.glue[stems[i]];
                    if (gf !== undefined && Q_STEM * FIELDS[gf].w > m.s) {
                        m = { s: Q_STEM * FIELDS[gf].w, field: gf };
                    }
                }
                var q = m.s;
                var wIdf = idfs[i], viaSynonym = false;
                // Второе написание («3д» → «3d»): берём лучшее из двух, а вес —
                // по редкости того, что совпало (латинское «3d» есть у многих).
                if (altOf && altOf[sig[i]]) {
                    var aw = altOf[sig[i]], ma = matchWord(doc, aw, stem(aw), i === lastIdx || sig.length === 1);
                    if (ma.s > q) { m = ma; q = ma.s; wIdf = Math.min(idfs[i], idf(prep, stem(aw))); }
                }
                if (q < 0.2) {
                    // Слово не найдено напрямую — пробуем его синонимы (со скидкой).
                    for (var j = 0; j < syn.length; j++) {
                        if (syn[j].from !== i) continue;
                        var ms = matchWord(doc, syn[j].w, syn[j].w, false);
                        if (ms.s * 0.75 > q) {
                            q = ms.s * 0.75; m = ms; viaSynonym = true;
                            // Вес — по РЕДКОСТИ синонима, а не исходного слова.
                            // «песню» → «аудио» не должно весить как «песню»:
                            // «аудио» есть у сотни инструментов, и без этого
                            // «обрезать песню» вытаскивало всю аудио-полку.
                            wIdf = Math.min(idfs[i], idf(prep, syn[j].w));
                        }
                    }
                }
                // Слово-тип закрыто РОЛЬЮ. «голос в текст» и «Аудио в текст»:
                // слова «голос» в инструменте нет, но пара «звук → текст» у
                // него совпала целиком, а голос — это звук. Без этого у
                // правильного ответа закрыто одно слово из двух, и его
                // обгоняли инструменты, где «голос» просто встречается.
                if (q < 0.2 && roleFull) {
                    var wt = typeOf(sig[i]);
                    if (wt && qRoleTypes[wt]) { q = 0.6; m = { s: 0.6, field: 1 }; }
                }
                if (q >= 0.2) {
                    // Покрытием синоним считается, только если он сам сужает
                    // выбор. Иначе достаточно любого «аудио» в тексте, чтобы
                    // попасть в тир точных ответов.
                    // ⚠️ Слово, найденное ТОЛЬКО в intro, покрытия само по себе
                    // не создаёт — оно его лишь дополняет (см. ниже). intro —
                    // SEO-проза и самое шумное поле (вес 0.22), а покрытие —
                    // ПЕРВЫЙ ключ сортировки. Без этого инструмент, у которого
                    // оба слова случайно встретились в прозе, обгонял тот, у
                    // которого слово стоит в НАЗВАНИИ: «восстановление
                    // изображений» → Декодер QR-кода OTP (cov=2, счёт 2.68)
                    // выше «Восстановления фото» (cov=1, счёт 4.39).
                    if (isKey[i] && (!viaSynonym || wIdf >= KEY_IDF)) {
                        if (m.field < FIELDS.length - 1) covered++; else introOnly++;
                    }
                    if (m.field < FIELDS.length - 1) strong++;
                    if (isKey[i] && !viaSynonym) direct++;
                    score += q * wIdf * (isConn[i] ? 0.33 : 1);
                    if (m.field === 0 && !isConn[i]) nameHits++;
                }
            }

            // Проза ДОПОЛНЯЕТ совпадение, но не составляет его целиком: если
            // хоть одно слово нашлось в названии/ключевых/описании, слова из
            // intro тоже идут в покрытие. Полный запрет (intro не даёт
            // покрытия никогда) замерен и отвергнут — он ронял голдсет на
            // «убрать шум с записи», где «запись» живёт только в прозе.
            if (strong && introOnly) covered += introOnly;

            if (!covered) continue;

            // Полное покрытие запроса — главный сигнал. Инструмент, ответивший на
            // все слова, обязан стоять выше того, кто поймал одно частое слово.
            var cov = covered / keyIdx.length;
            score *= (0.35 + 0.65 * cov) * (1 + 0.55 * cov);

            // Прямое попадание слова ценнее попадания по синониму, но это вес, а
            // не отдельный тир: у «Теста AV-синхронизации» слово «звучание»
            // стоит буквально, и жёсткий приоритет прямых совпадений ставил его
            // выше «Теста динамиков» на запросе «тест звучания».
            score *= 0.78 + 0.22 * (direct / keyIdx.length);

            // Слова запроса, стоящие рядом и в документе рядом, — сильный сигнал
            // того, что это ровно та формулировка, а не два случайных попадания.
            if (phrasePairs.length) {
                var phrase = 0;
                for (var p = 0; p < phrasePairs.length; p++) {
                    var best = 0;
                    for (var pf = 0; pf < FIELDS.length && best < FIELDS[pf].w; pf++) {
                        var jn = doc.f[FIELDS[pf].key].joined;
                        for (var pn = 0; pn < phrasePairs[p].length; pn++) {
                            if (jn.indexOf(phrasePairs[p][pn]) >= 0) { best = FIELDS[pf].w; break; }
                        }
                    }
                    phrase += best;
                }
                if (phrase > 0) score *= 1 + 0.4 * (phrase / phrasePairs.length);
            }
            if (wholeNeedles.length) {
                var whole = 0;
                for (var wf = 0; wf < FIELDS.length - 1 && !whole; wf++) {
                    var wj = doc.f[FIELDS[wf].key].joined;
                    for (var wn = 0; wn < wholeNeedles.length; wn++) {
                        if (wj.indexOf(wholeNeedles[wn]) >= 0) { whole = FIELDS[wf].w; break; }
                    }
                }
                if (whole) score *= 1 + 0.6 * whole;
            }

            // Направление (см. «Направление: из ЧЕГО во ЧТО»). Совпавшая пара
            // «вход → выход» весит как ещё одно закрытое слово — это и есть
            // ответ на запрос, а не случайное попадание; объявленная, но
            // другая пара теряет столько же. Покрытие — первый ключ
            // сортировки, и только через него роль может обогнать инструмент,
            // у которого просто больше совпавших слов.
            var covAdj = 0;
            if (rc) {
                covAdj = rc.cov;
                score *= rc.mul;
            }

            // Несовпадение носителя. Запрос назвал тип данных («картинку»), а
            // название инструмента — только ДРУГИЕ типы («…из видео»): это
            // соседний инструмент для другого материала. «убрать текст с
            // картинки» → «Убрать текст из видео», «добавить текст на
            // картинку» → «Субтитры к видео», «обрезать часть аудиозаписи» →
            // «Обрезка видео» — все три стояли первыми (замер 23.09.2026).
            if (qTypes.length && doc.nameTypes) {
                var mediumOk = false;
                for (var mt = 0; mt < qTypes.length && !mediumOk; mt++) {
                    for (var mn = 0; mn < doc.nameTypes.length; mn++) {
                        if (sameMedium(qTypes[mt], doc.nameTypes[mn])
                            || inputCompatible(qTypes[mt], doc.nameTypes[mn])) { mediumOk = true; break; }
                    }
                }
                if (!mediumOk) score *= 0.6;
            }

            // «Удалить водяной знак» — это не «Водяной знак». Название, где
            // совпал ПРЕДМЕТ запроса, у инструмента, который его накладывает,
            // перебивало инструменты удаления: «Водяной знак» стоял первым, над
            // «Убрать текст из видео» и «Удалить объект с фото» (письмо
            // 28.09.2026, «не открыли»). Роли тут не помогут — удаление их не
            // даёт намеренно (см. «Направление»). Поэтому отдельно: запрос
            // велит убрать, а инструмент нигде о себе не говорит, что убирает,
            // — это соседний инструмент для противоположной задачи.
            if (qRemove && nameHits > 0 && !doc.removes) score *= 0.6;

            // Заголовок, начинающийся с запроса, и точное совпадение имени.
            // nameShare — какая доля слов НАЗВАНИЯ закрыта запросом. Это
            // нормализация по длине заголовка (аналог field-length norm в BM25):
            // «конвертер» покрывает «Конвертер файлов» наполовину, а «Конвертер
            // длины и веса филамента» — на пятую часть, и короткое общее
            // название обязано стоять выше при прочих равных.
            var nameShare = nameHits / (doc.f.name.tok.length || 1);
            if (doc.nameNorm === qNorm) score *= 2.2;
            else if (doc.nameNorm.lastIndexOf(qNorm, 0) === 0) score *= 1.5 * (1 + 0.12 * nameShare);
            // Слово в НАЗВАНИИ весомее того же слова в ключевых словах: и
            // «Шумоочистка звука», и «Очиститель AI-музыки» держат в kw фразу
            // «убрать шум», но про шум по названию — только первая.
            else if (nameHits > 0) score *= (1 + 0.25 * (nameHits / sig.length)) * (1 + 0.12 * nameShare);

            // Популярность разводит инструменты, одинаково подходящие под запрос
            // («субтитры» → Автосубтитры прежде Живых субтитров). Её вес тем
            // больше, чем размытее запрос, — см. popWeight выше.
            score *= 1 + popWeight * (doc.pop / 100);

            // Навигационный буст хаба (`search_boost` в config/tools → поле `b`
            // индекса). Однословный «конвертер» одинаково подходит двум десяткам
            // конвертеров: популярность там разводит СОСЕДЕЙ по нише, но лучший
            // ответ на общий запрос — универсальный инструмент, а он может быть
            // новым и «непопулярным». Так крупные поисковики поднимают хабовую
            // страницу на навигационных запросах. Вес задаёт конфиг; движок его
            // только применяет. Специфичные запросы защищены покрытием (первый
            // ключ сортировки) и точным совпадением имени (×2.2 выше буста).
            // На запросе-преобразовании («mp4 в mp3») хаб не навигационный
            // ответ: человек назвал, что во что, и узкий инструмент с ровно
            // этой парой точнее универсального.
            if (doc.boost && !(qRoles && qRoles.conv.length)) score *= 1 + doc.boost;

            out.push({ t: doc.t, s: score, cov: Math.max(1, covered + covAdj), direct: direct, pop: doc.pop });
        }

        // Опечатки. Добираем не только когда точный проход пуст: «стенографи»
        // цепляет что-то по вхождению в SEO-текст, но нужная «Стеганография»
        // находится только по похожести. Порог держим высоким — это добор, а не
        // основной канал.
        //
        // Сравниваем с ИСХОДНЫМИ словами (`raw`), а не с починенными: у
        // «стенографи» починка выбирает «стенограмма» (→ Речь в текст, ответ
        // разумный), но по похожести к самому «стенографи» ближе всего
        // «Стеганография» — ради неё этот проход и написан. Считать похожесть
        // от подменённого слова значит потерять второй смысл запроса.
        // `noFuzzy` — для внутренних прогонов разреза слипшегося слова: там
        // смотрят только на res[0].cov и res[0].s, а добор по опечаткам сам по
        // себе стоит дороже всего остального скоринга (перебор всех документов
        // × всех полей с биграммами). Замер на RU-индексе: разрез
        // «фоторедактор» — 52 мс с добором против 18 мс без него, и это на
        // десктопе, в главном потоке живого выпадающего списка.
        if (out.length < 5 && !opts.noFuzzy) {
            var already = Object.create(null);
            for (var a = 0; a < out.length; a++) already[out[a].t.url] = 1;
            for (var f = 0; f < prep.docs.length; f++) {
                var dd = prep.docs[f];
                if (already[dd.t.url]) continue;
                var tot = 0;
                for (var w = 0; w < raw.length; w++) {
                    var b = 0, qlen = raw[w].length;
                    // intro (последнее поле) в переборе по опечаткам не участвует:
                    // это самый длинный текст, а опечатку ловят название и
                    // ключевые слова. Без него добор дешевле втрое.
                    for (var ff = 0; ff < FIELDS.length - 1; ff++) {
                        // Сравниваем исходные слова, не основы: стеммер срезает
                        // как раз тот хвост, в котором сидит опечатка («Шумаме»
                        // → «Шумомер» видно только целиком). И без веса поля —
                        // иначе опечатка в названии не добирает до порога.
                        var tk2 = dd.f[FIELDS[ff].key].tok;
                        for (var z = 0; z < tk2.length; z++) {
                            // Опечатка не меняет длину слова сильно — отсев по
                            // длине снимает подавляющую часть сравнений.
                            if (Math.abs(tk2[z].length - qlen) > 3) continue;
                            var sc = bigram(raw[w], tk2[z]);
                            if (ff > 1) sc *= 0.8;
                            if (sc > b) b = sc;
                        }
                    }
                    tot += b;
                }
                tot /= raw.length;
                if (tot > 0.42) out.push({ t: dd.t, s: tot * 0.4, cov: 0, direct: 0, pop: dd.pop, fuzzy: true });
            }
        }

        var maxCov = 0;
        for (var c = 0; c < out.length; c++) if (out[c].cov > maxCov) maxCov = out[c].cov;

        out.sort(function (a, b) {
            if (b.cov !== a.cov) return b.cov - a.cov;       // покрытие — первый ключ
            if (Math.abs(b.s - a.s) > 1e-9) return b.s - a.s;
            return b.pop - a.pop;                            // равные — популярнее выше
        });

        // Отсечка хвоста. Цель — не «показать поменьше» любой ценой, а не давать
        // выдаче выродиться в список всех инструментов, где встретилось одно
        // частое слово («тест звучания» → 97 любых тестов). Поэтому режем по
        // относительной слабости, а не по фиксированному числу: слабый результат
        // остаётся, если рядом нет заведомо более сильных.
        var full = [], partial = [];
        for (var o = 0; o < out.length; o++) (out[o].cov >= maxCov ? full : partial).push(out[o]);

        // Однословный запрос вроде «тест» подходит под сотню инструментов по
        // букве, но «Найдено: 129» — это не ответ, а свалка. Там режем жёстче:
        // отличать релевантное от случайного всё равно нечем, кроме силы
        // совпадения и популярности.
        var floor = opts.tight ? 0.25 : (sig.length === 1 ? 0.3 : 0.12);
        var bestScore = full.length ? full[0].s : 0;
        full = full.filter(function (x) { return x.s >= bestScore * floor; });

        // Частичное покрытие — только хвостом и только если полных ответов мало.
        var keepPartial = Math.max(0, (opts.limit || 24) - full.length);
        if (maxCov > 1 && keepPartial > 0) {
            var pBest = partial.length ? partial[0].s : 0;
            partial = partial.filter(function (x) { return x.s >= pBest * 0.45; }).slice(0, keepPartial);
        } else {
            partial = [];
        }

        var result = full.concat(partial);
        // Что именно исправили — для подписи «Показаны результаты по …».
        // Свойство на массиве, а не новая форма возврата: у search() три
        // вызывающих места, и менять контракт ради подсказки нельзя.
        if (repaired) result.repaired = repaired;
        // Сколько значимых слов было в запросе — чтобы вызывающий мог
        // отличить ответ от совпадения по одному слову из четырёх (помощник
        // на главной говорит тогда «такого инструмента нет, вот близкое»).
        result.keyCount = keyIdx.length;
        return result;
    }

    /**
     * Выдача СЛАБАЯ — то есть спасать её ещё нужно.
     *
     * Пустая выдача — не единственная форма промаха, и это стоило нам целого
     * класса запросов. Добор по опечаткам ниже срабатывает на `out.length < 5`
     * и подмешивает документы с покрытием 0 — «похожие по написанию». Для
     * вызывающего это выглядело как НАЙДЕННОЕ: `scored.length !== 0`, значит
     * fallback() не зовётся, значит ни роутер формы, ни перебивка раскладки,
     * ни разрез слипшегося слова не пробуются вовсе.
     *
     * Живой случай (жалоба 27.08, скриншот): «фоторедактор» — слипшееся слово,
     * которое разрезается в «фото редактор» и находит полку редакторов. Вместо
     * этого человек видел «ВОЗМОЖНО, ВЫ ИСКАЛИ: Порядок страниц PDF, Проверка
     * англицизмов» — восемь карточек с покрытием 0, набранных по биграммной
     * похожести. Спасение существовало и было выключено ровно тем, что должно
     * было ему помогать.
     *
     * Признак — покрытие: документ с cov 0 не совпал с запросом НИ ОДНИМ
     * значимым словом, он попал в список только по написанию.
     */
    function weak(results) {
        if (!results || !results.length) return true;
        for (var i = 0; i < results.length; i++) if (!results[i].fuzzy) return false;
        return true;
    }

    /**
     * Спасение с оглядкой на то, что уже нашлось.
     *
     * Пустую выдачу спасает любой fallback() — хуже пустоты ничего нет. Со
     * слабой (одни похожие по написанию) так нельзя: замерено на 2795 живых
     * парах «запрос → что человек открыл», безусловная замена похожих на любой
     * разрез/перебивку роняет top1 49.7 → 49.4 и MRR 0.643 → 0.639. Причина
     * видна на «auddio»: добор по опечаткам находит там Audio Joiner, то есть
     * ровно то, что нужно, а разрез слипшегося слова подставляет вместо него
     * другой список таких же приблизительных совпадений.
     *
     * Поэтому обмен разрешён только вверх: похожие по написанию уступают место
     * НАСТОЯЩЕМУ совпадению (карточка роутера, термин, или список, в котором
     * есть документ с покрытием), но не другому списку похожих.
     *
     * ЦЕНА, выраженная в прогонах поиска по индексу — детерминированная мера,
     * в отличие от секундомера (машина под чужой нагрузкой давала разброс в
     * десять раз, и мерить на ней было нечего):
     *
     *   обычный запрос     1 → 1   ничего не платит: weak() отвечает «нет» на
     *                              первом же результате с покрытием
     *   пустая выдача      2 → 2   спасение и раньше пробовалось
     *   слабая выдача      1 → 3   перебивка раскладки + один разрез
     *
     * То есть втрое платит ровно тот ввод, который раньше отвечал мусором.
     * Внутренние прогоны разреза идут без добора по опечаткам (opts.noFuzzy),
     * то есть дешевле полного, а хедер дебаунсит ввод на 80 мс — это одно
     * срабатывание, а не очередь.
     */
    function rescue(query, prep, opts, scored) {
        if (!weak(scored)) return null;
        var fb = fallback(query, prep, opts);
        if (!fb) return null;
        if (!scored || !scored.length) return fb;       // пусто — хуже уже не будет
        // Узнанная форма строки (номер рейса, IP) и термин чужой письменности
        // — это не «другое прочтение», а точный ответ; они бьют похожих всегда.
        if (fb.type === 'route' || fb.type === 'term') return fb;
        // Прочтение (разрез слипшегося слова, чужая раскладка) вытесняет
        // похожих по написанию только при ПОЛНОМ покрытии — когда найденный
        // инструмент отвечает на ВСЕ слова прочитанной строки. Планка куплена
        // регрессией: «Федя» находило Ёфикатор по написанию, а перебивка
        // раскладки давала «atlz», у которого нашёлся документ с покрытием 1,
        // и человек вместо своего ответа получал Игру в города. Покрытие 1 у
        // придуманной строки — норма, а не признак попадания.
        return (fb.results && fb.results.length && fb.results[0].cov >= 2) ? fb : null;
    }

    function bigram(a, b) {
        if (a === b) return 1;
        if (a.length < 2 || b.length < 2) return 0;
        var bg = new Map(), i, s, m = 0;
        for (i = 0; i < a.length - 1; i++) { s = a.substr(i, 2); bg.set(s, (bg.get(s) || 0) + 1); }
        for (i = 0; i < b.length - 1; i++) { s = b.substr(i, 2); var c = bg.get(s); if (c > 0) { bg.set(s, c - 1); m++; } }
        return (2 * m) / (a.length + b.length - 2);
    }

    /* ------------------------------------------------------------------ *
     * Спасение промаха: роутер по ФОРМЕ строки + перебивка раскладки
     *
     * Разбор телеметрии `search_miss` за 26.07–05.08 показал, что запросы,
     * не нашедшие ничего, — это в основном НЕ «опишу задачу словами». Это
     * введённые ДАННЫЕ (номер рейса, IP, штрихкод, MAC) и текст, набранный в
     * чужой раскладке. Инструмент под каждый такой случай у нас есть — его
     * просто не находит словарный поиск: «su0731» не встречается в ключевых
     * словах Трекера рейсов, а «jghtltkbnm rk.x b ,gv» — это «определить ключ
     * и бпм» на латинице.
     *
     * ОБА механизма — только фолбэк на ПУСТУЮ выдачу. Это не украшение, а
     * то, что делает их безопасными: «mp3 320» формально подходит под шаблон
     * рейса (2 символа + 3 цифры), но по нему есть нормальные результаты, и
     * до роутера дело не доходит.
     * ------------------------------------------------------------------ */

    // ЙЦУКЕН ↔ QWERTY по позиции клавиши. Обе строки — без повторов, поэтому
    // обратная карта строится из той же пары. Точка/запятая различаются: «ю»
    // сидит на клавише «.», а «.» — на «/».
    var LAYOUT_EN = "qwertyuiop[]asdfghjkl;'zxcvbnm,./`";
    var LAYOUT_RU = "йцукенгшщзхъфывапролджэячсмитьбю.ё";

    // Карты РАЗДЕЛЬНЫЕ и направление выбирается по алфавиту строки. Одной
    // двусторонней картой не обойтись: «.» есть в обеих раскладках (в русской
    // это клавиша «/»), и запись ru→en затирала en→ru — «ключ» превращался в
    // «кл/ч», ровно на том запросе, ради которого перебивка и делается.
    var _flipEnRu = null, _flipRuEn = null;
    function flipMaps() {
        if (!_flipEnRu) {
            _flipEnRu = Object.create(null);
            _flipRuEn = Object.create(null);
            for (var i = 0; i < LAYOUT_EN.length; i++) {
                _flipEnRu[LAYOUT_EN[i]] = LAYOUT_RU[i];
                _flipRuEn[LAYOUT_RU[i]] = LAYOUT_EN[i];
            }
        }
        return { en: _flipEnRu, ru: _flipRuEn };
    }

    var HAS_CYR = /[а-яё]/;

    /**
     * ⚠️ Здесь стоял ГЕЙТ на перебивку: «искать по перебитой строке только если
     * индекс знает хоть одно её слово». Он снимал ~15 мс с каждого слабого
     * запроса и был отвергнут замером — 34 регрессии на живых парах «запрос →
     * что человек открыл».
     *
     * Причина в том, что дешёвая проверка не может предсказать матчинг. «vfcnt»
     * это «масте» — недопечатанный «мастеринг»: не основа, не целое слово, ни
     * одной записи в `df`. Поиск его находит (префиксный матч живого ввода,
     * близкая основа, синонимы), а O(1)-проверка по словарю — нет. Так же
     * ушли «rhb» → «кри», «rktq» → «клей», «xbybnm» → «чинить».
     *
     * Урок общий: гейт перед поиском обязан быть НЕ СТРОЖЕ самого поиска, иначе
     * он отсекает ровно то, ради чего поиск умнее словаря. Цена перебивки —
     * один прогон, и она платится только на слабой выдаче.
     */

    /**
     * Перебить строку в другую раскладку. Направление — по тому, что уже есть
     * в строке: кириллица значит «набрано в русской, хотели английскую».
     * Возвращает '' если менять нечего (например, запрос на хангыле).
     */
    function flipLayout(s) {
        var src = String(s || '').toLowerCase();
        var map = HAS_CYR.test(src) ? flipMaps().ru : flipMaps().en;
        var out = '', changed = 0;
        for (var i = 0; i < src.length; i++) {
            var ch = src[i], to = map[ch];
            if (to) { out += to; changed++; } else { out += ch; }
        }
        return changed ? out : '';
    }

    // Распространённые TLD + любой двухбуквенный ccTLD. Нужен, чтобы «readme.txt»
    // и «config.json» не уезжали в Whois: без этого шаблон домена ловит любое
    // слово с точкой. Расширения кода двухбуквенные (js, ts, py, go, sh, md)
    // отсекаются отдельно — как ccTLD они формально валидны, но в поисковой
    // строке это почти всегда имя файла.
    var TLD_OK = /^(com|net|org|io|dev|app|ai|co|info|biz|me|tv|cc|xyz|online|site|store|shop|tech|cloud|pro|name|mobi|edu|gov|mil|int|ru|su|рф|[a-z]{2})$/;
    var TLD_NO = /^(js|ts|py|go|sh|md|css|php)$/;

    var ROUTES = [
        // Номер рейса: код авиакомпании (2 буквы / буква+цифра / цифра+буква) +
        // 3–4 цифры. Ровно эта форма доминирует в промахах: su0731, lh2559,
        // s7-5031, u6-210, «5p 005». Две цифры не берём — «h264» и «x265» тоже
        // подошли бы под код+номер.
        {
            kind: 'flight', slug: 'flight-tracker', param: 'flight',
            re: /^([a-z]{2}|[a-z]\d|\d[a-z])[\s-]?(\d{3,4})$/i,
            value: function (m) { return (m[1] + m[2]).toUpperCase(); }
        },
        // IPv4 с проверкой октетов — «5.1» (звуковая конфигурация) не пройдёт.
        {
            kind: 'ip', slug: 'ip-lookup', param: 'ip',
            re: /^((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/,
            value: function (m) { return m[0]; }
        },
        // MAC — ДО IPv6, и это не косметика порядка. Шаблон IPv6 ниже («0–4
        // hex через двоеточия, 3–8 групп») формально накрывает и
        // «4c:bd:8f:73:55:02»: шесть групп по два hex-символа ему подходят, а
        // первым в списке выигрывал он. Живой промах 11.08 уехал именно так —
        // человек искал MAC, а получил карточку /ip-lookup. Обратной ошибки
        // быть не может: MAC-шаблон требует РОВНО шесть групп по два символа,
        // а такой «IPv6» — это 48 бит, то есть не адрес.
        {
            kind: 'mac', slug: 'mac-address-lookup', param: 'mac',
            re: /^([0-9a-f]{2}[:-]){5}[0-9a-f]{2}$|^([0-9a-f]{4}\.){2}[0-9a-f]{4}$/i,
            value: function (m) { return m[0].toUpperCase(); }
        },
        // IPv6 — минимум два двоеточия, иначе это «12:30».
        {
            kind: 'ip', slug: 'ip-lookup', param: 'ip',
            re: /^[0-9a-f]{0,4}(:[0-9a-f]{0,4}){2,7}$/i,
            value: function (m) { return m[0].toLowerCase(); }
        },
        // EAN-8 / UPC-A / EAN-13 / GTIN-14. Формат подставляем по длине —
        // /barcode-generator принимает и валидирует набранный код.
        {
            kind: 'barcode', slug: 'barcode-generator', param: 'code',
            re: /^(\d{8}|\d{12,14})$/,
            value: function (m) { return m[0]; },
            extra: function (m) {
                var n = m[0].length;
                return { fmt: n === 8 ? 'ean8' : (n === 12 ? 'upca' : (n === 13 ? 'ean13' : 'ean13')) };
            }
        },
        {
            kind: 'domain', slug: 'whois-lookup', param: 'domain',
            re: /^(?!-)([a-z0-9-]{1,63}\.)+([a-z]{2,24})$/i,
            value: function (m) { return m[0].toLowerCase(); },
            ok: function (m) {
                var tld = m[2].toLowerCase();
                return TLD_OK.test(tld) && !TLD_NO.test(tld);
            }
        },
        {
            kind: 'color', slug: 'color-converter', param: 'color',
            re: /^#([0-9a-f]{3}|[0-9a-f]{6})$/i,
            value: function (m) { return m[0].toLowerCase(); }
        }
    ];

    /**
     * Термин на письменности, для которой у нас НЕТ локали.
     *
     * Замер `search_miss` за 30 дней (14.08): китайские запросы приходят
     * регулярно и все — к инструментам, которые у нас есть: 二维码 (QR),
     * 幻灯片 (слайд-шоу), 对讲机 (рация), 坏点 (битые пиксели), 五线谱 (нотный
     * стан). Найтись они не могут ничем: индекс собирается из переводов, а
     * `stripForeignScripts()` вычищает иероглифы из ключевых слов КАЖДОЙ
     * локали — то есть этих строк нет ни в одном из девяти индексов
     * (проверено). Поэтому спасение здесь, рядом с роутером формы: маленькая
     * измеренная таблица «строка → слаг», а не попытка научить поиск китайскому.
     *
     * ⚠️ Слаг обязан существовать, быть public и попадать в индекс — это же
     * условие, что у ROUTES, и проверяется тем же тестом
     * (`SearchRouterTest::test_router_targets_are_public_and_listed`).
     * Инструмента под «电流表» (амперметр) у нас нет, поэтому его в таблице
     * нет: увести человека в соседний прибор хуже, чем не найти.
     */
    var TERMS = {
        '二维码': 'qr-code',
        '幻灯片': 'slideshow-maker',
        '对讲机': 'walkie-talkie',
        '坏点检测': 'dead-pixel-test',
        '坏点': 'dead-pixel-test',
        '五线谱': 'sheet-music-to-midi',
        // Замер `search_miss` за 90 дней (17.09.2026): те же письменности, те
        // же пустые выдачи, инструмент под каждую строку у нас есть.
        '声乐去除': 'vocal-remover',
        '音频分离': 'stem-splitter',
        '在线识谱': 'sheet-music-to-midi',
        '识谱': 'sheet-music-to-midi',
        '楽器': 'instrument-identifier',
        // Письмо «Что хотят сделать» 28.09.2026 (tembrica): «视频转音频» (видео
        // в аудио) и «乐谱识别» (распознать ноты) — пустая выдача.
        '视频转音频': 'extract-audio',
        '提取音频': 'extract-audio',
        '乐谱识别': 'sheet-music-to-midi'
    };

    // Письменности без пробелов: японец печатает «曲で使われてる楽器をしりたい»
    // одной строкой, и термин сидит ВНУТРИ неё. Для них точного совпадения
    // мало — ищем вхождение, но только если найденный термин ОДИН: две разные
    // цели в одной строке означают, что мы не поняли вопрос.
    var CJK_RE = /[\u3040-\u30FF\u3400-\u4DBF\u4E00-\u9FFF]/;

    /**
     * Слаг по термину: точное совпадение, а если его нет — единственный
     * термин, который начинается с набранного (живой промах был «坏点检» —
     * человек не дописал четвёртый иероглиф).
     */
    function termSlug(query) {
        var q = String(query || '').trim().toLowerCase();
        if (!q || q.length > 24) return null;
        if (TERMS[q]) return TERMS[q];
        var hit = null, k;
        for (k in TERMS) {
            if (k.length > q.length && k.lastIndexOf(q, 0) === 0) {
                if (hit && hit !== TERMS[k]) return null;   // неоднозначно — молчим
                hit = TERMS[k];
            }
        }
        if (hit || !CJK_RE.test(q)) return hit;
        for (k in TERMS) {
            if (k.length < q.length && q.indexOf(k) >= 0) {
                if (hit && hit !== TERMS[k]) return null;   // два термина в строке — не поняли
                hit = TERMS[k];
            }
        }
        return hit;
    }

    /**
     * Узнать строку по форме. Возвращает {kind, slug, param, value, extra} или null.
     * Чистая функция — её гоняют юнит-тесты без индекса.
     */
    function route(query) {
        var q = String(query || '').trim();
        if (!q || q.length > 64) return null;
        for (var i = 0; i < ROUTES.length; i++) {
            var r = ROUTES[i], m = q.match(r.re);
            if (!m) continue;
            if (r.ok && !r.ok(m)) continue;
            return {
                kind: r.kind, slug: r.slug, param: r.param,
                value: r.value(m), extra: r.extra ? r.extra(m) : null
            };
        }
        return null;
    }

    // Инструмент по слагу — из САМОГО индекса, а не из зашитого URL: адрес
    // локалезависим (/ru/…, /en/…), и захардкоженный увёл бы на чужую локаль.
    // Заодно это значит, что скрытый или снятый с публикации инструмент
    // просто не найдётся, и карточка не появится.
    function resolveTool(prep, slug) {
        var suffix = '/' + slug;
        for (var i = 0; i < prep.docs.length; i++) {
            var u = prep.docs[i].t.url || '';
            if (u.length > suffix.length && u.slice(-suffix.length) === suffix) return prep.docs[i].t;
        }
        return null;
    }

    /* ------------------------------------------------------------------ *
     * Разговорный запрос
     *
     * «как мне обрезать песню» — это не запрос из двух слов, а запрос из
     * двух слов в обёртке из вопроса. Скоринг ищет ВСЕ значимые слова, и
     * «как»/«мне» либо не находятся нигде, либо размывают покрытие — выдача
     * пустая, хотя нужный тул в каталоге есть.
     *
     * Раньше такие строки приносил только тот, кто печатает фразами. С
     * голосовым вводом (кнопка микрофона в поисковой строке) это становится
     * НОРМОЙ: вслух никто не диктует «обрезать песню», вслух говорят
     * предложением. Поэтому обёртка снимается здесь, в движке, а не в UI —
     * иначе три поверхности поиска разошлись бы в ответах.
     *
     * Срезаем ТОЛЬКО с начала и только служебные слова: значимый глагол
     * («обрезать», «открыть») остаётся, иначе от запроса ничего не будет.
     * Наполнители («пожалуйста», «онлайн») убираем где угодно.
     * ------------------------------------------------------------------ */
    var LEAD_STOP = {
        ru: 'как каким чем что чтобы где куда зачем мне я нам нужен нужна нужно надо хочу хочется помоги подскажи покажи открой найди найти сделай сделать можно ли есть какой какая какое какие программа приложение сервис инструмент сайт для',
        en: 'how do does i to can could you please find show me want need a an the tool app site service where what is there any',
        de: 'wie kann ich man bitte finde zeige brauche will ein eine der die das tool werkzeug programm seite wo was gibt es',
        es: 'como cómo puedo quiero necesito buscar busco encontrar muestrame muéstrame por favor un una el la herramienta programa sitio donde dónde que qué hay',
        fr: 'comment je peux veux besoin trouver cherche montre moi svp un une le la outil programme site ou où quoi est il y a', uk: 'як яким чим що щоб де куди навіщо мені я нам потрібен потрібна потрібно треба хочу хочеться допоможи підкажи покажи відкрий знайди знайти зроби зробити можна чи є який яка яке які програма застосунок сервіс інструмент сайт для',
        pt: 'como posso quero preciso encontrar buscar mostre me por favor um uma o a ferramenta programa site onde que qual tem',
        id: 'bagaimana cara saya ingin mau butuh cari mencari tolong sebuah alat aplikasi situs di mana apa ada untuk',
        ko: '어떻게 나는 내가 좀 찾아 찾아줘 알려줘 하고 싶어 필요해 도구 사이트 어디',
        ar: 'كيف أريد اريد احتاج أحتاج من فضلك ابحث عن أداة اداة موقع أين اين ما هل',
        tr: 'nasıl ne nerede nereden niçin niye bana ben bize lazım gerek gerekli istiyorum yardım et göster bul bulabilir miyim misin var mı hangi program uygulama servis araç site için',
    };
    var FILLER = {
        ru: 'пожалуйста онлайн бесплатно срочно быстро',
        en: 'please online free quickly',
        de: 'bitte online kostenlos gratis schnell',
        es: 'favor online linea línea gratis rapido rápido',
        fr: 'ligne gratuit rapidement', uk: 'будь ласка онлайн безкоштовно терміново швидко',
        pt: 'favor online gratis grátis rapido rápido',
        id: 'online gratis cepat',
        ko: '온라인 무료',
        ar: 'أونلاين اونلاين مجاني بسرعة',
        tr: 'lütfen online çevrimiçi ücretsiz bedava acil hemen hızlı çabuk',
    };
    /* Списки прогоняются через тот же normalize(), что и запрос: иначе «ё» и
       «й» в словаре не совпадут с «е»/«и» в нормализованном токене. */
    var _stopCache = {};
    function stopSets(locale) {
        var key = locale || 'en';
        if (_stopCache[key]) return _stopCache[key];
        function toSet(str) {
            var s = Object.create(null);
            var parts = String(str || '').split(/\s+/);
            for (var i = 0; i < parts.length; i++) {
                if (parts[i]) s[normalize(parts[i])] = 1;
            }
            return s;
        }
        var made = {
            lead: toSet(LEAD_STOP[key] || LEAD_STOP.en),
            fill: toSet(FILLER[key] || FILLER.en),
        };
        _stopCache[key] = made;
        return made;
    }

    /**
     * Снять разговорную обёртку с УЖЕ разобранных на слова запроса.
     * Возвращает укороченный массив слов или null, если снимать нечего.
     */
    function stripIntentTokens(words, locale) {
        if (!words || words.length < 2) return null;  /* одно слово обёрткой не бывает */
        var sets = stopSets(locale);

        var kept = [];
        for (var i = 0; i < words.length; i++) {
            if (!sets.fill[words[i]]) kept.push(words[i]);
        }
        var j = 0;
        while (j < kept.length && sets.lead[kept[j]]) j++;
        /* Все слова служебные — это не запрос, а вежливость. Возвращаем null,
           а не пустой список: пустой запрос в search() вернул бы весь каталог,
           то есть худший из возможных ответов на «как мне». */
        if (j >= kept.length) return null;
        var out = kept.slice(j);
        if (out.length === words.length) return null;  /* ничего не сняли */
        return out;
    }

    /** Строковая обёртка над stripIntentTokens — её проверяют юнит-тесты. */
    function stripIntent(query, locale) {
        var out = stripIntentTokens(tokenize(query), locale);
        return out ? out.join(' ') : null;
    }

    /**
     * Единая точка спасения пустой выдачи для всех трёх поверхностей (хедер,
     * /search, сайдбар). Держим её здесь по той же причине, по которой сюда
     * съехал скоринг: три копии логики разъедутся, и одна строка снова начнёт
     * давать три разных ответа.
     *
     * Возвращает:
     *   {type:'route',  kind, value, tool, url}   — узнали форму строки;
     *   {type:'layout', query, results}           — перебивка раскладки нашла;
     *   {type:'split',  query, results}           — слипшиеся слова, разделили;
     *   null                                      — спасать нечем.
     */
    function fallback(query, prep, opts) {
        var q = String(query || '').trim();
        if (!q || !prep) return null;

        var hit = route(q);
        if (hit) {
            var tool = resolveTool(prep, hit.slug);
            if (tool) {
                var url = tool.url + '?' + hit.param + '=' + encodeURIComponent(hit.value);
                if (hit.extra) {
                    for (var k in hit.extra) url += '&' + k + '=' + encodeURIComponent(hit.extra[k]);
                }
                return { type: 'route', kind: hit.kind, value: hit.value, tool: tool, url: url };
            }
        }

        // Термин на письменности, для которой у нас нет локали (см. TERMS).
        // Отдаём в форме списка результатов, а НЕ карточкой-роутером: у роутера
        // подпись объясняет, что мы распознали («номер рейса», «IP-адрес»), а
        // тут объяснять нечего — это просто нужный инструмент. Пустой `query`
        // гасит строку «возможно, вы имели в виду» во всех трёх поверхностях.
        var tslug = termSlug(q);
        if (tslug) {
            var tTool = resolveTool(prep, tslug);
            if (tTool) return { type: 'term', query: '', results: [{ t: tTool, s: 1 }] };
        }

        var flipped = flipLayout(q);
        if (flipped && flipped !== q.toLowerCase()) {
            var res = search(flipped, prep, opts || {});
            if (res.length) return { type: 'layout', query: flipped, results: res };
        }

        var split = splitGlued(q, prep, opts || {});
        if (split) return split;

        /* Разговорной обёртки здесь НЕТ намеренно: её снимает сам search()
           до скоринга, поэтому до пустой выдачи такой запрос не доживает.
           Ветка здесь была бы мёртвым кодом. */
        return null;
    }

    /**
     * Слипшиеся слова: «объединитьвидео», «улучшениеаудио», «тестгеймпада».
     *
     * Это не опечатка и не другая раскладка — человек просто не поставил
     * пробел (чаще всего с телефона). Для поиска это один незнакомый токен, и
     * выдача пуста: замер 14.08 по `search_miss` — «объединитьвидео» 0
     * результатов, «объединить видео» 24.
     *
     * Перебираем ВСЕ точки разреза и берём лучшую по счёту первого результата,
     * а не первую попавшуюся: у «объединитьвидео» подходит и «объедини
     * тьвидео» (первая половина найдёт «объединить видео» по стеммингу), но
     * осмысленный разрез даёт заметно более высокий счёт. Обе половины — от
     * 4 символов: короче начинаются ложные разрезы вроде «объ единить».
     *
     * ⚠️ Прогонять search() на КАЖДОЙ точке разреза нельзя: замерено 350–700 мс
     * на русском запросе (и это десктоп — на телефоне главный поток встанет на
     * пару секунд). Поэтому сначала дешёвый отсев по `prep.df`: обе половины
     * обязаны быть известными основами, а это O(1). Обычно остаётся один
     * кандидат, и поиск запускается один раз.
     */
    /** Знает ли индекс такое слово — по основе или по достижимости (близкая
     *  основа / живой префикс), тем же правилом, что и починка опечаток. */
    function known(half, prep) {
        var w = normalize(half);
        if (!w) return false;
        var st = stem(w);
        if (prep.df[st]) return true;
        ensureRepairIndex(prep);
        return reachable(w, st, prep);
    }

    /**
     * Опечатка в ОДНОМ слове — не слипшиеся два.
     *
     * Разрез и добор по опечаткам ловят одну и ту же строку: «soundbard»
     * режется в «sound bard» (обе половины индексу знакомы, покрытие полное — и
     * человек уезжает в Отпугиватель собак), хотя на самом деле это
     * «soundboard». То же «trasponer» → «tras poner» вместо «transponer».
     * Признак — близкий сосед в словаре: у настоящего слипшегося слова
     * («фоторедактор», «аудиомикшер», «тестгеймпада») такого соседа нет, у
     * опечатки он есть и очень близкий (0.89 и 0.94 на этих двух).
     */
    var GLUE_TYPO = 0.8;
    function looksLikeTypo(q, prep) {
        ensureRepairIndex(prep);
        var list = prep.vocab[q.slice(0, 2)];
        for (var i = 0; list && i < list.length; i++) {
            if (Math.abs(list[i].length - q.length) > 2) continue;
            if (bigram(q, list[i]) >= GLUE_TYPO) return true;
        }
        return false;
    }

    function splitGlued(query, prep, opts) {
        var q = String(query || '').trim().toLowerCase();
        if (q.length < 9 || q.length > 32 || /[\s\d]/.test(q)) return null;
        /* Только буквы одной письменности: «su6718» и «4c:bd:8f» сюда не едут,
           их разбирает роутер формы выше. */
        if (!/^[a-zа-яё]+$/i.test(q)) return null;
        if (looksLikeTypo(q, prep)) return null;

        // Отбор разреза смотрит только на первый результат — добор по
        // опечаткам в этих прогонах не нужен и стоит втрое дороже скоринга.
        var innerOpts = { tight: opts && opts.tight, limit: opts && opts.limit, noFuzzy: true };
        var best = null;
        for (var i = 4; i <= q.length - 4; i++) {
            /* ⚠️ Основу берём с НОРМАЛИЗОВАННОЙ половины, иначе отсев врёт:
               normalize() складывает «й»→«и», индекс держит «геимпад», а
               stem('геймпада') даёт «геймпад» — и живой запрос
               «тестгеймпада» отсеивался как незнакомый. */
            if (!known(q.slice(0, i), prep) || !known(q.slice(i), prep)) continue;
            var candidate = q.slice(0, i) + ' ' + q.slice(i);
            var res = search(candidate, prep, innerOpts);
            // Разрез засчитываем ТОЛЬКО когда обе половины нашли один и тот же
            // инструмент. «Обе половины — известные основы» этого не значит:
            // «стенография» режется в «стено графия» (обе основы индексу
            // знакомы), лучший результат отвечает на одну половину, и человек
            // вместо Стеганографии получал Проверку орфографии. Полное
            // покрытие отличает настоящее слипшееся слово — «фото|редактор»,
            // «аудио|микшер», «объединить|видео» — от случайного распила.
            if (!res.length || res[0].cov < 2) continue;
            var score = res[0].s || 0;
            if (!best || score > best.score) {
                best = { type: 'split', query: candidate, results: res, score: score };
            }
        }
        if (best) delete best.score;
        return best;
    }

    /* ------------------------------------------------------------------ *
     * Запрос на ЧУЖОЙ письменности
     *
     * Индекс локали держит только свою письменность и латиницу
     * (stripForeignScripts), поэтому кириллица на /en или арабица на /de не
     * находят НИЧЕГО — хотя тот же инструмент есть в русском, украинском,
     * арабском или корейском индексе. Живые случаи (журнал помощника, intl, три
     * недели до 30.09.2026): «русский», «мастеринг», «раскадровка» на /en; и
     * iPhone с Google-переводчиком на tembrica.com/en — страница переведена,
     * человек пишет по-русски («узнать сколько русских слов я знаю») и видит
     * «ничего не найдено».
     *
     * Возвращает локаль, в чьём индексе искать, или null. Решает большинство
     * букв запроса (от 60 %): в «mp3 в мр4» письменность не одна, и туда не
     * надо. Кириллица — ru, но uk, если есть «і ї є ґ»: в русском их нет.
     * ------------------------------------------------------------------ */
    var SCRIPT_LOCALES = [
        { re: /[а-яё]/, loc: 'ru', own: { ru: 1, uk: 1 } },
        { re: /[іїєґ]/, loc: 'uk', own: { ru: 1, uk: 1 } },
        { re: /[؀-ۿ]/, loc: 'ar', own: { ar: 1 } },
        { re: /[ᄀ-ᇿ㄰-㆏가-힯]/, loc: 'ko', own: { ko: 1 } }
    ];
    var LATIN_LETTER = /[a-zÀ-ɏ]/;
    function otherScriptLocale(query, pageLocale) {
        var q = String(query || '').toLowerCase(), latin = 0, n = [0, 0, 0, 0], i, s;
        for (i = 0; i < q.length; i++) {
            if (LATIN_LETTER.test(q[i])) { latin++; continue; }
            for (s = 0; s < SCRIPT_LOCALES.length; s++) if (SCRIPT_LOCALES[s].re.test(q[i])) { n[s]++; break; }
        }
        // Украинские «і ї є ґ» считаются вместе с остальной кириллицей.
        var cyr = n[0] + n[1], letters = latin + cyr + n[2] + n[3];
        if (!letters) return null;
        if (cyr * 10 >= letters * 6) return SCRIPT_LOCALES[0].own[pageLocale] ? null : (n[1] ? 'uk' : 'ru');
        if (n[2] * 10 >= letters * 6) return pageLocale === 'ar' ? null : 'ar';
        if (n[3] * 10 >= letters * 6) return pageLocale === 'ko' ? null : 'ko';
        return null;
    }

    /**
     * Стоит ли на этой пустой выдаче предлагать «опишите инструмент — сделаем».
     *
     * Пустая выдача бывает двух разных сортов, и предложение уместно только на
     * одной. Замер по `search_miss` за 05–09.08 (первые дни после того, как
     * заработал роутер форм выше): из 33 действительно пустых запросов 16 —
     * обломки данных, а не просьба: «55.755864, 37.617698», «su67», «91-775»,
     * «74hc», «s73». Человеку, набравшему половину номера рейса, «мы сделаем вам
     * инструмент» читается как издёвка, а в очередь заявок приносит мусор.
     *
     * Признак фразы — слово хотя бы из трёх букв: китайское «二维码» проходит,
     * а IP, MAC, штрихкод и артикул отсекаются. Порог именно 3, а не 2: «su67»,
     * «s73», «hr46x» — это укороченные коды, и на двух буквах гейт их пропустил
     * бы. Живёт здесь, а не в трёх вызывающих файлах, по той же причине, что и
     * fallback(): три копии правила разъедутся.
     */
    var _phraseRe;
    function requestable(query) {
        var q = String(query || '').trim();
        if (q.length < 3) return false;
        if (_phraseRe === undefined) {
            // \p{L} требует флага u (ES2018). Если движок старый — лучше
            // показать предложение лишний раз, чем не показать никогда.
            try { _phraseRe = new RegExp('\\p{L}{3,}', 'u'); } catch (e) { _phraseRe = null; }
        }
        return _phraseRe ? _phraseRe.test(q) : true;
    }

    /**
     * ОТВЕТ на запрос-задачу — то, что показывает помощник «Что хотите
     * сделать?» на главной. Одна функция и для фонового потока
     * (search-worker.js), и для запасного пути в главном потоке: логика ответа
     * не должна жить в двух копиях.
     *
     * Возвращает сериализуемое состояние (его передают между потоками):
     *   { q, kind, list:[{t, cov, s, fuzzy}], route, dym, confident, n, top, keyCount }
     *   kind: hit | partial | fuzzy | route | term | layout | split | empty.
     */
    function lite(t) {
        return t ? { name: t.name, desc: t.desc, icon: t.icon, url: t.url, group: t.group } : null;
    }
    function answer(query, prep, opts) {
        opts = opts || { tight: true, limit: 8 };
        var q = String(query || '').replace(/\s+/g, ' ').trim();
        var res = search(q, prep, opts);
        var fb = rescue(q, prep, opts, res);
        var st = { q: q, kind: 'hit', list: [], route: null, dym: '', confident: false, keyCount: res.keyCount || 0 };
        var raw = [];
        if (fb && fb.type === 'route') {
            st.kind = 'route';
            st.route = { kind: fb.kind, value: fb.value, url: fb.url };
            raw = [{ t: fb.tool, cov: 1, s: 1 }];
        } else if (fb && fb.results) {
            st.kind = fb.type;
            raw = fb.results;
            st.dym = fb.query || '';
        } else if (!res.length) {
            st.kind = 'empty';
        } else {
            var allFuzzy = true;
            for (var i = 0; i < res.length; i++) if (!res[i].fuzzy) { allFuzzy = false; break; }
            st.kind = allFuzzy ? 'fuzzy' : 'hit';
            raw = res;
        }
        // Совпадение по одному слову из трёх — не ответ. «сделать из фото
        // мультфильм про кота» находил «Маджонг» по «коту», и назвать это
        // лучшим инструментом значило бы соврать. Честнее сказать, что такого
        // инструмента нет, показать близкое и предложить заказать.
        var kc = st.keyCount;
        if (st.kind === 'hit' && kc >= 3 && raw[0] && (raw[0].cov || 0) * 3 <= kc) st.kind = 'partial';
        // «Лучше всего подойдёт» — только когда первый ответ правда впереди:
        // закрывает больше слов запроса или заметно сильнее второго.
        var a = raw[0], b = raw[1];
        if (st.kind === 'route' || st.kind === 'term') st.confident = !!a;
        else if (a && st.kind !== 'fuzzy' && st.kind !== 'partial') {
            st.confident = !b || (a.cov || 0) > (b.cov || 0) || (a.s || 0) >= (b.s || 0) * 1.18;
        }
        for (var k = 0; k < raw.length && k < (opts.limit || 8); k++) {
            st.list.push({ t: lite(raw[k].t), cov: raw[k].cov || 0, s: raw[k].s || 0, fuzzy: !!raw[k].fuzzy });
        }
        st.n = st.list.length;
        st.top = [];
        for (var j = 0; j < st.list.length && j < 3; j++) st.top.push(String(st.list[j].t.url || '').split('?')[0].split('/').pop());
        return st;
    }

    return {
        prepare: prepare,
        search: search,
        answer: answer,
        weak: weak,
        rescue: rescue,
        route: route,
        fallback: fallback,
        requestable: requestable,
        otherScriptLocale: otherScriptLocale,
        flipLayout: flipLayout,
        stripIntent: stripIntent,
        // экспортируем внутренности — их проверяют юнит-тесты и бенчмарк
        _internal: { normalize: normalize, tokenize: tokenize, stem: stem, stemsNear: stemsNear, bigram: bigram, extractRoles: extractRoles, typeOf: typeOf }
    };
});
